import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  Suspense,
} from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
  Cell,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Legend,
} from "recharts";
import { useI18n } from "../i18n";

function isFetchAbortError(err: unknown): boolean {
  if (err instanceof DOMException && err.name === "AbortError") return true;
  return err instanceof Error && err.name === "AbortError";
}
import { PageLayout } from "../layouts/PageLayout";
import { Button } from "../components/ui/Button";
import { FilePicker } from "../components/ui/FilePicker";
import { SecurityCard } from "../components/ui/SecurityCard";
import { Select } from "../components/ui/Select";
import pageStyles from "../styles/pages/Compliance.module.css";
import { useAuth } from "../components/AuthProvider";
import useComplianceAnswers from "../hooks/useComplianceAnswers";
import ComplianceFormCard, {
  ComplianceFormCategory,
  ComplianceFormQuestion,
} from "../components/compliance/ComplianceForm";
import ExtendedQuestionnaire from "../components/compliance/ExtendedQuestionnaire";
import type { ExtendedQuestionnaireAnswer } from "../components/compliance/ExtendedQuestionnaire";
import AnnexAControlsPanel from "../components/compliance/AnnexAControlsPanel";
import GdpControlsPanel from "../components/compliance/GdpControlsPanel";
import DoraControlsPanel from "../components/compliance/DoraControlsPanel";
import EnsControlsPanel from "../components/compliance/EnsControlsPanel";
import Soc2ControlsPanel from "../components/compliance/Soc2ControlsPanel";
import Nis2ControlsPanel from "../components/compliance/Nis2ControlsPanel";
import PciDssControlsPanel from "../components/compliance/PciDssControlsPanel";
import SwiftCscfControlsPanel from "../components/compliance/SwiftCscfControlsPanel";
import { GDPR_QUESTIONNAIRES } from "../data/gdprQuestionnaires";
import { SOC2_QUESTIONNAIRES } from "../data/soc2Questionnaires";
import { ISO27001_QUESTIONNAIRES } from "../data/iso27001Questionnaires";
import { NIS2_FULL_QUESTIONNAIRES } from "../data/nis2FullQuestionnairesComplete";
import { ENS_MEDIO_QUESTIONNAIRES } from "../data/ensMedioQuestionnaires";
import { DORA_QUESTIONNAIRES } from "../data/doraQuestionnaires";
import { PCI_DSS_2025_SELF_ASSESSMENT_BLOCKS } from "../data/pciDss2025Questionnaires";
import { SWIFT_CSCF_2025_SELF_ASSESSMENT_BLOCKS } from "../data/swiftCscf2025Questionnaires";
import { SDLC_SECURITY_SELF_ASSESSMENT_BLOCKS } from "../data/sdlcSecurityQuestionnaires";
import { TECHNICAL_CONTROLS_SELF_ASSESSMENT_BLOCKS } from "../data/technicalControlsQuestionnaires";
import { BSI_DE_SELF_ASSESSMENT_BLOCKS, ANSSI_FR_SELF_ASSESSMENT_BLOCKS, KSBP_LT_SELF_ASSESSMENT_BLOCKS, NCA_EE_SELF_ASSESSMENT_BLOCKS } from "../data/nationalProfileQuestionnaires";
import { collapseStandardsForUx, isNis2CanonicalStandard } from "../utils/standardCanonicalUx";

/** Legacy extended self-assessment blocks (stored as NIS2_FULL) shown under canonical NIS2 only. */
function usesNis2ExtendedSelfAssessment(standardUpper: string | undefined): boolean {
  return isNis2CanonicalStandard(standardUpper || "");
}
import API_BASE_URL, { API_ENDPOINTS } from "../config/api";
import apiService from "../services/api";
import { featureFlags } from "../config/featureFlags";
// Dynamic import to avoid circular dependency issues
// Import type directly from types file to avoid barrell imports
import type { Recommendation } from "../utils/recommendationsTypes";
import { getActiveOrgId } from "../utils/orgUtils";
import { getWeakAreasFromExtended } from "../utils/weakAreasFromExtended";
import Iso42001Questionnaire from "../components/compliance/Iso42001Questionnaire";
import type { ExtendedQuestionnaireBlock } from "../components/compliance/ExtendedQuestionnaire";
import {
  groupQuickAnswersByCategory,
  isIso42001Selection,
  iso42001FullProgress,
  iso42001UiState,
  recommendationsFromIso42001Blocks,
} from "../utils/iso42001Questionnaire";
import { getCanonicalStandardCode } from "../utils/standardNormalizer";
import { ComplianceCenterShell } from "../modules/compliance-center";
import { buildDistributionChartData, buildTrendChartData } from "../modules/compliance-center/adapters/charts";

function isMigrationSupportedForStandard(formStandard: string | null | undefined): boolean {
  const code = getCanonicalStandardCode(formStandard || '')
  return code === 'GDPR' || code === 'SOC2' || code === 'DORA'
}

import { importWithReload, lazyWithReload } from "../utils/lazyWithReload";

// Lazy component declared at top level to avoid TDZ issues
// Renamed to avoid shadowing the default export name
const LazyRecommendationsPanel = lazyWithReload(
  () => import("../components/recommendations/RecommendationsPanel")
);

type SummaryCard = {
  totalHealth?: number;
  colorHint?: string | null;
  activeStandards?: number;
  lastUpdated?: string | null;
  auditReadiness?: string | null;
};

type SummaryDetail = {
  id: string;
  standard: string;
  name?: string | null;
  compliancePercentage: number;
  status?: string | null;
  lastAssessmentDate?: string | null;
};

type SummaryResponse = {
  cards?: SummaryCard | null;
  details?: SummaryDetail[];
  weakAreas?: Array<{ code?: string | null; name?: string | null; percent: number }>;
};

type AnalyticsPoint = {
  label: string;
  value: number;
};

type DistributionBucket = {
  score: number;
  count: number;
};

type SurveyRow = {
  id: string;
  control: string;
  standard: string;
  percent: number;
  category: string;
  lastUpdated?: string | null;
  controlsCount?: number | null;
};

type HistoryRow = {
  id: string;
  date: string;
  standard: string;
  statusBefore?: string | null;
  statusAfter?: string | null;
  changedBy?: string | null;
  changeReason?: string | null;
};

type StandardOption = {
  code: string;
  label: string;
  active?: boolean;
};

type AuditReadinessSummary = {
  readinessStatus: string;
  readinessScore: number;
  blockersCount: number;
  missingEvidenceCount: number;
  openCriticalRecommendations: number;
  driftEventsCount: number;
  evidenceRequestsCount: number;
  connectorsCount: number;
  certificationWorkflowsCount: number;
  certificationUnderReviewCount: number;
  certificationLatestStatus: string;
  aiRecommendationCount: number;
  aiReadinessNarrative: string;
  aiNextStepsCount: number;
  aiStaleEvidenceCount: number;
  aiMissingEvidenceCount: number;
  aiPolicyOverdueReviewCount: number;
};

const KPI_COLORS = {
  green: "#10b981",
  yellow: "#facc15",
  red: "#ef4444",
} as const;

const SCORE_COLORS: Record<string, string> = {
  "1": "#9ca3af",
  "2": "#ef4444",
  "3": "#3b82f6",
  "4": "#facc15",
  "5": "#10b981",
};

/** Recharts needs a fixed height; parent `.chartPlot` uses overflow:auto */
const COMPLIANCE_CHART_H = 260;
const EXTENDED_RADAR_H = 280;

const AUDIT_STATUS_WEIGHTS: Record<string, number> = {
  COMPLIANT: 100,
  PARTIAL: 75,
  NON_COMPLIANT: 40,
  NOT_ASSESSED: 15,
  UNKNOWN: 0,
};

const clampPercent = (value: number | null | undefined): number => {
  if (value == null || Number.isNaN(value)) return 0;
  return Math.max(0, Math.min(100, Math.round(Number(value))));
};

const humanize = (value: string): string =>
  value
    .replace(/[_\s]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
    .trim();

const truncateRadarLabel = (v: string, maxLen = 14): string => {
  if (!v) return "";
  return v.length > maxLen ? `${v.slice(0, Math.max(0, maxLen - 1))}…` : v;
};

const mapThresholdColor = (value: number, hint?: string | null): string => {
  if (hint && /^#([0-9A-F]{3}){1,2}$/i.test(hint)) return hint;
  if (value > 90) return KPI_COLORS.green;
  if (value >= 60) return KPI_COLORS.yellow;
  return KPI_COLORS.red;
};

const buildUrl = (
  base: string,
  params: Record<string, string | number | null | undefined>,
): string => {
  const url = new URL(base, window.location.origin);
  Object.entries(params).forEach(([key, value]) => {
    if (value === null || typeof value === "undefined" || value === "") return;
    url.searchParams.set(key, String(value));
  });
  return url.toString();
};

const normalizeQuestionLabel = (
  category: ComplianceFormCategory,
  question: ComplianceFormQuestion,
  language: string,
): string => {
  const coerce = (value: unknown): string | null => {
    if (typeof value === "string" && value.trim().length > 0) {
      return value.trim();
    }
    return null;
  };

  // ALWAYS prioritize text_en for English console, even if text is already set by backend
  const textEn = coerce(question.text_en);
  if (textEn) return textEn;

  // Then check if text is already resolved (from backend)
  const direct = coerce(question.text);
  if (direct) return direct;

  const localeCandidates = Array.from(
    new Set([
      language,
      language.split(/[-_]/)[0],
      `text_${language}`,
      `text_${language.split(/[-_]/)[0]}`,
      "text_en",
      "text_ru",
      "text_es",
      "textEn",
      "textRu",
      "textEs",
    ]),
  ).filter(Boolean);

  for (const key of localeCandidates) {
    const candidate = coerce(
      (question as Record<string, unknown>)[key as keyof typeof question],
    );
    if (candidate) return candidate;
  }

  const readableFromCode = (code?: string | null) => {
    if (!code) return null;
    const base = code.includes("_")
      ? code.split("_").slice(1).join(" ")
      : code;
    const formatted = base
      .replace(/[_-]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    if (!formatted) return null;
    return humanize(formatted);
  };

  return (
    readableFromCode(question.code) ??
    readableFromCode(category.code) ??
    question.code ??
    question.id
  );
};

interface ComplianceProps {
  embeddedMode?: boolean;
  /** Compliance Center Overview embed on enterprise dark workspace — hide duplicate KPI/audit strips. */
  enterpriseEmbedded?: boolean;
  selectedStandard?: string | null;
}

const Compliance: React.FC<ComplianceProps> = ({
  embeddedMode = false,
  enterpriseEmbedded = false,
  selectedStandard = null,
}) => {
  const compactEnterpriseEmbed = embeddedMode && enterpriseEmbedded;
  const { t, language: locale } = useI18n();
  const { user, token, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Refresh user on mount when org is missing so /auth/me can return organizationId/organizations
  useEffect(() => {
    if (!user) return;
    const hasOrg = !!getActiveOrgId() || (Array.isArray((user as any).organizations) && (user as any).organizations.length > 0) || !!(user as any).organizationId || (user as any).organization;
    if (!hasOrg && typeof refreshUser === 'function') {
      refreshUser();
    }
  }, []);

  const organizationId = useMemo(() => getActiveOrgId(), [user]);

  const authHeaders = useMemo(() => {
    const headers: Record<string, string> = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return headers;
  }, [token]);

  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [analytics, setAnalytics] = useState<AnalyticsPoint[]>([]);
  const [distribution, setDistribution] = useState<DistributionBucket[]>([]);
  const [surveyRows, setSurveyRows] = useState<SurveyRow[]>([]);
  const [historyRows, setHistoryRows] = useState<HistoryRow[]>([]);
  const [dashboardCategoryOptions, setDashboardCategoryOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [categoryBreakdown, setCategoryBreakdown] = useState<Record<string, any> | null>(null);
  const [formCategories, setFormCategories] = useState<ComplianceFormCategory[]>([]);
  const [expandedCategoryIds, setExpandedCategoryIds] = useState<string[]>([]);
  const [availableStandards, setAvailableStandards] = useState<StandardOption[]>([]);
  const [selectedStandards, setSelectedStandards] = useState<Set<string>>(new Set());
  const [dashboardStandard, setDashboardStandard] = useState<string | null>(null);
  const [dashboardCategory, setDashboardCategory] = useState<string | null>(null);
  const [formStandard, setFormStandard] = useState<string | null>(null);
  const [formCategoryCode, setFormCategoryCode] = useState<string | null>(null);
  const [isAllStandardsSelected, setIsAllStandardsSelected] = useState<boolean>(false);
  const [questionnaireType, setQuestionnaireType] = useState<'QUICK' | 'SELF_ASSESSMENT' | 'ANNEX_A_CONTROLS' | 'GDPR_CONTROLS' | 'DORA_CONTROLS' | 'ENS_CONTROLS' | 'SOC2_CONTROLS' | 'NIS2_CONTROLS' | 'PCI_DSS_2025_CONTROLS' | 'SWIFT_CSCF_2025_CONTROLS' | 'DOCUMENTS' | 'PROCESSES' | 'ROLES'>('SELF_ASSESSMENT');
  const [iso42001ReloadKey, setIso42001ReloadKey] = useState(0);
  const [isoQuickLoading, setIsoQuickLoading] = useState(false);
  const [isoQuickError, setIsoQuickError] = useState(false);
  const [isoQuickAvailable, setIsoQuickAvailable] = useState(false);
  const [isoFullLoading, setIsoFullLoading] = useState(false);
  const [isoFullSettled, setIsoFullSettled] = useState(false);
  const [isoFullError, setIsoFullError] = useState(false);
  const [isoFullAvailable, setIsoFullAvailable] = useState(false);
  const [isoFullBlocks, setIsoFullBlocks] = useState<ExtendedQuestionnaireBlock[]>([]);
  const [extendedAnswers, setExtendedAnswers] = useState<Record<string, ExtendedQuestionnaireAnswer>>({});
  const [extendedLoading, setExtendedLoading] = useState(false);
  const [extendedLastSaved, setExtendedLastSaved] = useState<string | null>(null);
  const [extendedQuestionnairesData, setExtendedQuestionnairesData] = useState<Record<string, {
    answers: Record<string, ExtendedQuestionnaireAnswer | string>;
    blockStats: Record<string, Record<string, number>>;
    overallStats: { yes: number; no: number; n_a: number; total: number };
  } | null>>({
    SELF_ASSESSMENT: null,
    DOCUMENTS: null,
    PROCESSES: null,
    ROLES: null,
    ANNEX_A_CONTROLS: null,
    GDPR_CONTROLS: null,
    DORA_CONTROLS: null,
    ENS_CONTROLS: null,
    SOC2_CONTROLS: null,
    NIS2_CONTROLS: null,
    PCI_DSS_2025_CONTROLS: null,
    SWIFT_CSCF_2025_CONTROLS: null,
  });
  const [dashboardRefreshKey, setDashboardRefreshKey] = useState(0);
  const [formRefreshKey, setFormRefreshKey] = useState(0);
  const [extendedDataRefreshKey, setExtendedDataRefreshKey] = useState(0);
  const [migrating, setMigrating] = useState(false);
  const [importFeedback, setImportFeedback] = useState<
    { type: "success" | "error"; message: string }
    | null
  >(null);
  const [isUploadingReport, setIsUploadingReport] = useState(false);
  const [selectedReportFile, setSelectedReportFile] = useState<File | null>(null);
  const [auditReadinessSummary, setAuditReadinessSummary] = useState<AuditReadinessSummary | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Read standard from URL query parameter on mount
  useEffect(() => {
    const standardFromUrl = searchParams.get('standard');
    if (standardFromUrl && !dashboardStandard) {
      setDashboardStandard(standardFromUrl);
    }
  }, [searchParams, dashboardStandard]);

  useEffect(() => {
    if (!embeddedMode) return;
    const incoming = (selectedStandard || "").trim();
    if (!incoming) return;
    if (incoming.toUpperCase() === "ALL") {
      setIsAllStandardsSelected(true);
      setDashboardStandard(null);
      setDashboardCategory(null);
      if (formStandard !== null) setFormStandard(null);
      setDashboardRefreshKey((prev) => prev + 1);
      setFormRefreshKey((prev) => prev + 1);
      return;
    }
    if (dashboardStandard !== incoming) {
      setIsAllStandardsSelected(false);
      setDashboardStandard(incoming);
      setDashboardCategory(null);
      setDashboardRefreshKey((prev) => prev + 1);
    }
    if (formStandard !== incoming) {
      setFormStandard(incoming);
      setFormCategoryCode(null);
      setFormRefreshKey((prev) => prev + 1);
    }
  }, [embeddedMode, selectedStandard, dashboardStandard, formStandard]);

  useEffect(() => {
    let cancelled = false;

    if (!organizationId) {
      setAvailableStandards([]);
      setDashboardStandard(null);
      setFormStandard(null);
      return () => {
        cancelled = true;
      };
    }

    const loadStandards = async () => {
      try {
        const response = await apiService.getComplianceStandards(organizationId);
        if (cancelled) return;
        const data = Array.isArray(response?.data) ? response.data : [];
        const mapped: StandardOption[] = data.map((standard) => ({
          code: standard.code,
          label: standard.name || standard.code,
          active: standard.active ?? false,
        }));
        setAvailableStandards(mapped);

        if (mapped.length > 0) {
          // Only auto-select first standard if user hasn't explicitly selected "All Standards"
          if (!isAllStandardsSelected) {
            if (
              !dashboardStandard ||
              (!mapped.some((item) => item.code === dashboardStandard) && dashboardStandard !== '')
            ) {
              setDashboardStandard(mapped[0].code);
            }
          }
          if (!formStandard || !mapped.some((item) => item.code === formStandard)) {
            setFormStandard(mapped[0].code);
          }
        } else {
          setDashboardStandard(null);
          setFormStandard(null);
        }
      } catch (err) {
        console.error("[Compliance] unable to load standards", err);
      }
    };

    loadStandards();

    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  // Load selected standards from StarterGuide
  useEffect(() => {
    const loadSelectedStandards = async () => {
      const normalizeStandardName = (n: string): string => {
        const upper = n.toUpperCase();
        if (upper === "ISO/IEC 42001:2023" || upper === "ISO/IEC 42001" || upper === "ISO 42001" || upper === "ISO42001" || upper === "ISO_42001") return "ISO/IEC 42001:2023";
        if (upper === "ISO/IEC 27001" || upper === "ISO 27001" || upper === "ISO27001") return "ISO 27001";
        if (upper === "PCI DSS" || upper === "PCI-DSS") return "PCI-DSS";
        if (upper === "PCI DSS 2025" || upper === "PCI_DSS_2025") return "PCI DSS 2025";
        if (upper === "SWIFT CSCF 2025" || upper === "SWIFT_CSCF_2025") return "SWIFT CSCF 2025";
        if (upper === "SOC 2" || upper === "SOC2") return "SOC 2";
        if (upper === "GDPR") return "GDPR";
        if (upper === "NIS2" || upper === "NIS 2") return "NIS 2";
        if (upper === "DORA") return "DORA";
        if (upper === "SDLC SECURITY" || upper === "SDLC_SECURITY") return "SDLC Security";
        if (upper === "TECHNICAL CONTROLS" || upper === "TECHNICAL_CONTROLS" || upper === "TECH_CONTROLS" || upper === "TECH CONTROLS") return "Technical Controls";
        if (upper === "CIS" || upper.startsWith("CIS CONTROLS") || upper === "CIS_CONTROLS_V8") return "CIS Controls v8";
        // National profiles
        if (upper === "ENS_ES" || upper === "NATIONAL_ENS_ES" || upper === "ENS") return "ENS_ES";
        if (upper === "BSI_DE" || upper === "BSI" || upper === "NATIONAL_BSI_DE") return "BSI_DE";
        if (upper === "ANSSI_FR" || upper === "NATIONAL_ANSSI_FR" || upper === "ANSSI" || upper === "ENS_ANSSI") return "ANSSI_FR";
        if (upper === "KSBP_LT" || upper === "KSBP" || upper === "NATIONAL_KSBP_LT") return "KSBP_LT";
        if (upper === "NCA_EE" || upper === "NCA" || upper === "NATIONAL_NCA_EE") return "NCA_EE";
        return n;
      };

      try {
        const sg = await apiService.getStarterGuideQuestionnaire();
        if (Array.isArray(sg.data?.relevantStandards) && sg.data.relevantStandards.length > 0) {
          const normalized = new Set(sg.data.relevantStandards.map(normalizeStandardName));
          setSelectedStandards(normalized);
          return;
        }
      } catch {}
      
      // Fallback: try localStorage
      try {
        const flags = JSON.parse(localStorage.getItem("starter_guide_flags") || "{}");
        if (Array.isArray(flags?.relevantStandards) && flags.relevantStandards.length > 0) {
          const normalized = new Set(flags.relevantStandards.map(normalizeStandardName));
          setSelectedStandards(normalized);
          return;
        }
      } catch {}
      
      // If no selected standards, show all (empty set means show all)
      setSelectedStandards(new Set());
    };
    loadSelectedStandards();
  }, []);

  // Controls / Annex A only for ISO 27001 — reset legacy *_CONTROLS questionnaire types
  useEffect(() => {
    const legacyControlsTypes = new Set([
      'SOC2_CONTROLS',
      'GDPR_CONTROLS',
      'DORA_CONTROLS',
      'ENS_CONTROLS',
      'NIS2_CONTROLS',
      'PCI_DSS_2025_CONTROLS',
      'SWIFT_CSCF_2025_CONTROLS',
      'CONTROLS',
    ]);
    if (legacyControlsTypes.has(questionnaireType)) {
      setQuestionnaireType('SELF_ASSESSMENT');
      return;
    }
    if (questionnaireType === 'ANNEX_A_CONTROLS') {
      const u = (formStandard || '').toUpperCase().trim();
      const isIso = u === 'ISO 27001' || u === 'ISO/IEC 27001' || u === 'ISO27001' || u === 'ISO_27001';
      if (!isIso) setQuestionnaireType('SELF_ASSESSMENT');
    }
  }, [formStandard, questionnaireType]);

  useEffect(() => {
    if (!isIso42001Selection(formStandard)) return;
    setQuestionnaireType((current) => (current === "QUICK" ? "QUICK" : "SELF_ASSESSMENT"));
  }, [formStandard]);

  useEffect(() => {
    if (!formCategoryCode) return;
    const exists = formCategories.some(
      (category) => category.code === formCategoryCode,
    );
    if (!exists) {
      setFormCategoryCode(null);
    }
  }, [formCategories, formCategoryCode]);


  // Normalize standard code for API (ISO 27001 -> ISO27001, GDPR -> GDPR)
  const standardCodeForApi = useMemo(() => {
    if (!formStandard) return formStandard;
    const u = String(formStandard).toUpperCase().trim();
    if (u === 'ISO 42001' || u === 'ISO/IEC 42001' || u === 'ISO/IEC 42001:2023' || u === 'ISO_42001' || u === 'ISO42001') return 'ISO42001';
    if (u === 'ISO 27001' || u === 'ISO/IEC 27001' || u === 'ISO_27001') return 'ISO27001';
    if (u === 'GDPR') return 'GDPR';
    if (u === 'DORA') return 'DORA';
    if (u === 'ENS_ES' || u === 'NATIONAL_ENS_ES' || u === 'ENS') return 'ENS_ES';
    if (u === 'SOC 2' || u === 'SOC2') return 'SOC2';
    if (u === 'NIS2' || u === 'NIS 2' || u === 'NIS2_FULL') return 'NIS2';
    if (u === 'PCI DSS 2025' || u === 'PCI_DSS_2025') return 'PCI_DSS_2025';
    if (u === 'SWIFT CSCF 2025' || u === 'SWIFT_CSCF_2025') return 'SWIFT_CSCF_2025';
    if (u === 'SDLC SECURITY' || u === 'SDLC_SECURITY') return 'SDLC_SECURITY';
    if (u === 'TECHNICAL CONTROLS' || u === 'TECHNICAL_CONTROLS' || u === 'TECH_CONTROLS' || u === 'TECH CONTROLS') return 'TECH_CONTROLS';
    if (u === 'BSI_DE' || u === 'BSI' || u === 'NATIONAL_BSI_DE') return 'BSI_DE';
    if (u === 'ANSSI_FR' || u === 'ANSSI' || u === 'ENS_ANSSI' || u === 'NATIONAL_ANSSI_FR') return 'ENS_ANSSI';
    if (u === 'KSBP_LT' || u === 'NATIONAL_KSBP_LT') return 'KSBP_LT';
    if (u === 'NCA_EE' || u === 'NCA' || u === 'NATIONAL_NCA_EE') return 'NCA_EE';
    if (u === 'CIS' || u.startsWith('CIS CONTROLS')) return 'CIS';
    return formStandard;
  }, [formStandard]);

  // Load extended questionnaire answers when standard/type changes
  useEffect(() => {
    const loadExtendedAnswers = async () => {
      if (!formStandard || !organizationId) {
        return;
      }

      try {
        setExtendedLoading(true);
        const response = await apiService.getExtendedQuestionnaire(
          standardCodeForApi ?? formStandard,
          questionnaireType,
          organizationId
        );
        if (response.success && response.data) {
          setExtendedAnswers(response.data.answers || {});
          setExtendedLastSaved(response.data.lastSavedAt || null);
        } else {
          setExtendedAnswers({});
          setExtendedLastSaved(null);
        }
      } catch (error) {
        console.error('Failed to load extended questionnaire:', error);
        setExtendedAnswers({});
        setExtendedLastSaved(null);
      } finally {
        setExtendedLoading(false);
      }
    };

    loadExtendedAnswers();
  }, [formStandard, standardCodeForApi, questionnaireType, organizationId]);

  const isISO27001Standard = useCallback((std: string | null): boolean => {
    if (!std) return false;
    const u = std.toUpperCase().trim();
    return u === 'ISO 27001' || u === 'ISO/IEC 27001' || u === 'ISO27001' || u === 'ISO_27001';
  }, []);

  const questionnaireTypeOptions = useMemo((): { value: string; label: string }[] => {
    if (!formStandard) return [];
    if (isIso42001Selection(formStandard)) {
      return [
        { value: "SELF_ASSESSMENT", label: t("compliance.forms.iso42001.full", "Full Self-Assessment") },
        { value: "QUICK", label: t("compliance.forms.iso42001.quick", "Quick Assessment") },
      ];
    }
    const opts: { value: string; label: string }[] = [
      {
        value: "SELF_ASSESSMENT",
        label: t("compliance.forms.types.fullSelfAssessment", "Full Self-Assessment Questionnaire"),
      },
    ];
    // Controls section is only for ISO 27001 Annex A — not for other standards
    if (isISO27001Standard(formStandard)) {
      opts.push({ value: "ANNEX_A_CONTROLS", label: t("compliance.forms.types.annexAControls", "Annex A Controls (93)") });
    }
    opts.push(
      { value: "DOCUMENTS", label: t("compliance.forms.types.documents", "Required Documents Checklist") },
      { value: "PROCESSES", label: t("compliance.forms.types.processes", "Required Processes Checklist") },
      { value: "ROLES", label: t("compliance.forms.types.roles", "Roles & Departments Checklist") },
    );
    return opts;
  }, [formStandard, t, isISO27001Standard]);

  // Load all extended questionnaires data for charts
  // Load when formStandard or dashboardStandard is set (for recommendations)
  useEffect(() => {
    const loadAllExtendedQuestionnaires = async () => {
      // Prefer dashboard filter for charts when specific standard selected
      const standardForCharts = (dashboardStandard && dashboardStandard !== 'all' && !isAllStandardsSelected)
        ? dashboardStandard
        : formStandard || dashboardStandard;
      const standardUpper = standardForCharts?.toUpperCase();
      const isGDPR = standardUpper === 'GDPR';
      const isSOC2 = standardUpper === 'SOC 2' || standardUpper === 'SOC2';
      // Support both NIS2 (regular) and NIS2_FULL for full questionnaire
      const isNIS2Full = usesNis2ExtendedSelfAssessment(standardUpper);
      const isENS = standardUpper === 'NATIONAL_ENS_ES' || standardUpper === 'ENS' || standardUpper === 'ENS_ES';
      const isDORA = standardUpper === 'DORA';
      const isISO27001 = standardUpper === 'ISO27001' || standardUpper === 'ISO 27001' || standardUpper === 'ISO/IEC 27001';
      const isPciDss = standardUpper === 'PCI DSS 2025' || standardUpper === 'PCI_DSS_2025';
      const isSwiftCscf = standardUpper === 'SWIFT CSCF 2025' || standardUpper === 'SWIFT_CSCF_2025';
      const isSdlcSecurity = standardUpper === 'SDLC SECURITY' || standardUpper === 'SDLC_SECURITY';
      const isTechControls = standardUpper === 'TECHNICAL CONTROLS' || standardUpper === 'TECHNICAL_CONTROLS' || standardUpper === 'TECH_CONTROLS' || standardUpper === 'TECH CONTROLS' || standardUpper === 'TECHNICAL_CONTROLS';
      const isNationalProfile = ['BSI_DE', 'ANSSI_FR', 'ENS_ANSSI', 'KSBP_LT', 'NCA_EE'].includes(standardUpper || '');
      const isCIS = standardUpper === 'CIS' || (standardUpper || '').includes('CIS CONTROLS');
      const isISO42001 = isIso42001Selection(standardUpper);
      const isSupported = isGDPR || isSOC2 || isNIS2Full || isENS || isDORA || isISO27001 || isPciDss || isSwiftCscf || isSdlcSecurity || isTechControls || isNationalProfile || isCIS || isISO42001;
      
      console.log('[Compliance] loadAllExtendedQuestionnaires called:', {
        formStandard,
        standardForCharts,
        standardUpper,
        isGDPR,
        isSOC2,
        isNIS2Full,
        isENS,
        isDORA,
        isSupported,
        organizationId,
      });
      
      if (!standardForCharts || !isSupported || !organizationId) {
        console.log('[Compliance] Skipping extended questionnaires load:', {
          reason: !standardForCharts ? 'no standard' : !isSupported ? 'not supported standard' : 'no organizationId',
        });
        setExtendedQuestionnairesData({
          SELF_ASSESSMENT: null,
          DOCUMENTS: null,
          PROCESSES: null,
          ROLES: null,
          ANNEX_A_CONTROLS: null,
          GDPR_CONTROLS: null,
          DORA_CONTROLS: null,
          ENS_CONTROLS: null,
          SOC2_CONTROLS: null,
          NIS2_CONTROLS: null,
          PCI_DSS_2025_CONTROLS: null,
          SWIFT_CSCF_2025_CONTROLS: null,
        });
        return;
      }
      
      console.log(`[Compliance] Starting to load extended questionnaires for ${standardForCharts}`);

      const types: Array<'SELF_ASSESSMENT' | 'DOCUMENTS' | 'PROCESSES' | 'ROLES' | 'ANNEX_A_CONTROLS' | 'GDPR_CONTROLS' | 'DORA_CONTROLS' | 'ENS_CONTROLS' | 'SOC2_CONTROLS' | 'NIS2_CONTROLS' | 'PCI_DSS_2025_CONTROLS' | 'SWIFT_CSCF_2025_CONTROLS'> = isISO42001
        ? ['SELF_ASSESSMENT']
        : isISO27001
        ? ['SELF_ASSESSMENT', 'DOCUMENTS', 'PROCESSES', 'ROLES', 'ANNEX_A_CONTROLS']
        : isGDPR || isDORA || isENS || isSOC2 || isNIS2Full
          ? ['SELF_ASSESSMENT', 'DOCUMENTS', 'PROCESSES', 'ROLES']
          : isPciDss || isSwiftCscf || isSdlcSecurity || isTechControls || isNationalProfile || isCIS
            ? ['SELF_ASSESSMENT']
            : ['SELF_ASSESSMENT', 'DOCUMENTS', 'PROCESSES', 'ROLES'];
      const loadedData: typeof extendedQuestionnairesData = {
        SELF_ASSESSMENT: null,
        DOCUMENTS: null,
        PROCESSES: null,
        ROLES: null,
        ANNEX_A_CONTROLS: null,
        GDPR_CONTROLS: null,
        DORA_CONTROLS: null,
        ENS_CONTROLS: null,
        SOC2_CONTROLS: null,
        NIS2_CONTROLS: null,
        PCI_DSS_2025_CONTROLS: null,
        SWIFT_CSCF_2025_CONTROLS: null,
      };

      await Promise.all(
        types.map(async (type) => {
          try {
            console.log(`[Compliance] Loading ${type} questionnaire for standard ${standardForCharts}, orgId ${organizationId}`);
                  const standardCode = isISO27001 ? 'ISO27001' : isGDPR ? 'GDPR' : isDORA ? 'DORA' : isENS ? 'ENS_ES' : isSOC2 ? 'SOC2' : isNIS2Full ? 'NIS2' : isPciDss ? 'PCI_DSS_2025' : isSwiftCscf ? 'SWIFT_CSCF_2025' : isSdlcSecurity ? 'SDLC_SECURITY' : isTechControls ? 'TECH_CONTROLS' : isNationalProfile ? standardUpper : standardForCharts;
            const response = await apiService.getExtendedQuestionnaire(
              standardCode,
              type,
              organizationId
            );
            console.log(`[Compliance] Response for ${type}:`, response);
            if (response.success && response.data) {
              // Check if data is actually empty (all empty objects/zeros)
              const hasData = Object.keys(response.data.answers || {}).length > 0 || 
                             Object.keys(response.data.blockStats || {}).length > 0 ||
                             (response.data.overallStats?.total || 0) > 0;
              
              if (hasData) {
                loadedData[type] = {
                  answers: response.data.answers || {},
                  blockStats: response.data.blockStats || {},
                  overallStats: response.data.overallStats || { yes: 0, no: 0, n_a: 0, total: 0 },
                };
                console.log(`[Compliance] Loaded ${type} questionnaire data:`, loadedData[type]);
              } else {
                console.log(`[Compliance] ${type} questionnaire exists but has no data (empty answers/stats)`);
                loadedData[type] = null;
              }
            } else {
              console.log(`[Compliance] No data for ${type} questionnaire (response.success=${response.success})`, response);
              loadedData[type] = null;
            }
          } catch (error) {
            console.error(`[Compliance] Failed to load extended questionnaire ${type}:`, error);
          }
        })
      );

      console.log('[Compliance] Final loaded questionnaires data:', loadedData);

      setExtendedQuestionnairesData(loadedData);
    };

    loadAllExtendedQuestionnaires();
  }, [formStandard, dashboardStandard, organizationId, extendedDataRefreshKey]);

  // Helper function to calculate block stats from answers if blockStats is missing
  const calculateBlockStatsFromAnswers = useCallback((answers: Record<string, ExtendedQuestionnaireAnswer>, blocks: ExtendedQuestionnaireBlock[]) => {
    console.log('[Compliance] calculateBlockStatsFromAnswers called:', {
      answersCount: Object.keys(answers).length,
      blocksCount: blocks.length,
      blockIds: blocks.map(b => b.id),
      sampleAnswers: Object.entries(answers).slice(0, 5),
    });
    const stats: Record<string, { yes: number; no: number; partial: number; n_a: number }> = {};
    blocks.forEach((block) => {
      stats[block.id] = { yes: 0, no: 0, partial: 0, n_a: 0 };
      block.questions.forEach((question) => {
        const answer = answers[question.id];
        if (answer === 'yes') stats[block.id].yes++;
        else if (answer === 'no') stats[block.id].no++;
        else if (answer === 'partial') stats[block.id].partial++;
        else if (answer === 'n_a') stats[block.id].n_a++;
      });
      console.log(`[Compliance] Block ${block.id} stats:`, stats[block.id], `(questions: ${block.questions.length})`);
    });
    console.log('[Compliance] Calculated blockStats:', stats);
    return stats;
  }, []);

  // Prepare radar data for all questionnaire types (moved outside IIFE)
  const selfAssessmentRadarData = useMemo(() => {
    const data = extendedQuestionnairesData.SELF_ASSESSMENT;
    const standardForRadar = (dashboardStandard && dashboardStandard !== 'all' && !isAllStandardsSelected) ? dashboardStandard : formStandard;
            const standardUpper = standardForRadar?.toUpperCase();
            const isGDPR = standardUpper === 'GDPR';
            const isSOC2 = standardUpper === 'SOC 2' || standardUpper === 'SOC2';
            const isISO27001 = standardUpper === 'ISO27001' || standardUpper === 'ISO_27001' || standardUpper === 'ISO 27001' || standardUpper === 'ISO/IEC 27001';
            const isNIS2Full = usesNis2ExtendedSelfAssessment(standardUpper);
            const isENS = standardUpper === 'NATIONAL_ENS_ES' || standardUpper === 'ENS' || standardUpper === 'ENS_ES';
            const isDORA = standardUpper === 'DORA';
            const blocks = isIso42001Selection(standardForRadar) ? isoFullBlocks :
                           isGDPR ? GDPR_QUESTIONNAIRES.SELF_ASSESSMENT :
                           isSOC2 ? SOC2_QUESTIONNAIRES.SELF_ASSESSMENT :
                           isISO27001 ? ISO27001_QUESTIONNAIRES.SELF_ASSESSMENT :
                           isNIS2Full ? NIS2_FULL_QUESTIONNAIRES.SELF_ASSESSMENT :
                           isENS ? ENS_MEDIO_QUESTIONNAIRES.SELF_ASSESSMENT :
                           isDORA ? DORA_QUESTIONNAIRES.SELF_ASSESSMENT : null;
    
    if (!data || !blocks) {
      console.log('[Compliance] Self-Assessment: No data or blocks, returning empty array');
      return [];
    }

    // Use blockStats if available, otherwise calculate from answers
    let blockStats = data.blockStats || {};
    if (!blockStats || Object.keys(blockStats).length === 0) {
      console.log('[Compliance] Self-Assessment: blockStats empty, calculating from answers');
      blockStats = calculateBlockStatsFromAnswers(data.answers || {}, blocks);
    }

    console.log('[Compliance] Self-Assessment radar data calculation:', {
      hasData: !!data,
      hasBlocks: !!blocks,
      blocksLength: blocks.length,
      blockStats,
      blockStatsKeys: Object.keys(blockStats),
      blockIds: blocks.map(b => b.id),
      answersCount: Object.keys(data.answers || {}).length,
    });

    const result = blocks.map((block) => {
      const stats = blockStats[block.id] || { yes: 0, no: 0, partial: 0, n_a: 0 };
      // Calculate percentage: (yes * 1 + partial * 0.5) / (total - n_a) * 100
      // N/A questions are excluded from denominator
      const totalQuestions = block.questions.length;
      const applicableQuestions = totalQuestions - (stats.n_a || 0); // Exclude N/A from denominator
      const score = (stats.yes || 0) * 1 + (stats.partial || 0) * 0.5; // Yes=1, Partial=0.5, No=0
      const percent = applicableQuestions > 0 
        ? Math.round((score / applicableQuestions) * 100) 
        : 0;
      
      console.log(`[Compliance] Block ${block.id} (${block.name_en}):`, {
        totalQuestions,
        applicableQuestions,
        yes: stats.yes,
        no: stats.no,
        partial: stats.partial,
        n_a: stats.n_a,
        score,
        percent,
      });
      
      return {
        subject: locale === 'ru' ? block.name_ru : locale === 'es' ? block.name_es : block.name_en,
        value: percent,
        fullMark: 100,
      };
    });
    
    console.log('[Compliance] Self-Assessment radar result:', result);
    return result;
  }, [extendedQuestionnairesData.SELF_ASSESSMENT, formStandard, dashboardStandard, isAllStandardsSelected, locale, calculateBlockStatsFromAnswers, isoFullBlocks]);

  const documentsRadarData = useMemo(() => {
    const data = extendedQuestionnairesData.DOCUMENTS;
    const standardForRadar = (dashboardStandard && dashboardStandard !== 'all' && !isAllStandardsSelected) ? dashboardStandard : formStandard;
            const standardUpper = standardForRadar?.toUpperCase();
            const isGDPR = standardUpper === 'GDPR';
            const isSOC2 = standardUpper === 'SOC 2' || standardUpper === 'SOC2';
            // Support both NIS2 (regular) and NIS2_FULL for full questionnaire
            const isNIS2Full = usesNis2ExtendedSelfAssessment(standardUpper);
            const isENS = standardUpper === 'NATIONAL_ENS_ES' || standardUpper === 'ENS' || standardUpper === 'ENS_ES';
            const isDORA = standardUpper === 'DORA';
            const isISO27001 = standardUpper === 'ISO27001' || standardUpper === 'ISO_27001' || standardUpper === 'ISO 27001' || standardUpper === 'ISO/IEC 27001';
            const blocks = isGDPR ? GDPR_QUESTIONNAIRES.DOCUMENTS : 
                           isSOC2 ? SOC2_QUESTIONNAIRES.DOCUMENTS :
                           isISO27001 ? ISO27001_QUESTIONNAIRES.DOCUMENTS :
                           isNIS2Full ? NIS2_FULL_QUESTIONNAIRES.DOCUMENTS :
                           isENS ? ENS_MEDIO_QUESTIONNAIRES.DOCUMENTS :
                           isDORA ? DORA_QUESTIONNAIRES.DOCUMENTS : null;
    console.log('[Compliance] Documents radar data calculation:', {
      dataExists: !!data,
      blocksExists: !!blocks,
      blockStatsExists: !!data?.blockStats,
      answersExists: !!data?.answers,
      blockStatsKeys: data?.blockStats ? Object.keys(data.blockStats) : [],
      blockIds: blocks?.map(b => b.id) || [],
    });

    if (!data || !blocks) {
      console.log('[Compliance] Documents: No data or blocks, returning empty array');
      return [];
    }

    let effectiveBlockStats = data.blockStats;
    if (!effectiveBlockStats || Object.keys(effectiveBlockStats).length === 0) {
      console.log('[Compliance] Documents: blockStats missing or empty, recalculating from answers.');
      effectiveBlockStats = calculateBlockStatsFromAnswers(data.answers || {}, blocks);
    }

    const result = blocks.map((block) => {
      const stats = effectiveBlockStats[block.id] || { yes: 0, no: 0, partial: 0, n_a: 0 };
      const totalQuestionsInBlock = block.questions.length;
      const applicableQuestions = totalQuestionsInBlock - (stats.n_a || 0); // Exclude N/A from denominator
      const score = (stats.yes || 0) * 1 + (stats.partial || 0) * 0.5; // Yes=1, Partial=0.5, No=0
      const percent = applicableQuestions > 0 ? Math.round((score / applicableQuestions) * 100) : 0;
      console.log(`[Compliance] Documents Block ${block.id}: stats=${JSON.stringify(stats)}, totalQuestions=${totalQuestionsInBlock}, percent=${percent}`);
      return {
        subject: locale === 'ru' ? block.name_ru : locale === 'es' ? block.name_es : block.name_en,
        value: percent,
        fullMark: 100,
      };
    });
    console.log('[Compliance] Documents radar result:', result);
    return result;
  }, [extendedQuestionnairesData.DOCUMENTS, formStandard, dashboardStandard, isAllStandardsSelected, locale, calculateBlockStatsFromAnswers]);

  const processesRadarData = useMemo(() => {
    const data = extendedQuestionnairesData.PROCESSES;
    const standardForRadar = (dashboardStandard && dashboardStandard !== 'all' && !isAllStandardsSelected) ? dashboardStandard : formStandard;
            const standardUpper = standardForRadar?.toUpperCase();
            const isGDPR = standardUpper === 'GDPR';
            const isSOC2 = standardUpper === 'SOC 2' || standardUpper === 'SOC2';
            // Support both NIS2 (regular) and NIS2_FULL for full questionnaire
            const isNIS2Full = usesNis2ExtendedSelfAssessment(standardUpper);
            const isENS = standardUpper === 'NATIONAL_ENS_ES' || standardUpper === 'ENS' || standardUpper === 'ENS_ES';
            const isDORA = standardUpper === 'DORA';
            const isISO27001 = standardUpper === 'ISO27001' || standardUpper === 'ISO_27001' || standardUpper === 'ISO 27001' || standardUpper === 'ISO/IEC 27001';
            const blocks = isGDPR ? GDPR_QUESTIONNAIRES.PROCESSES : 
                           isSOC2 ? SOC2_QUESTIONNAIRES.PROCESSES :
                           isISO27001 ? ISO27001_QUESTIONNAIRES.PROCESSES :
                           isNIS2Full ? NIS2_FULL_QUESTIONNAIRES.PROCESSES :
                           isENS ? null : // ENS doesn't have separate Processes questionnaire
                           isDORA ? DORA_QUESTIONNAIRES.PROCESSES : isENS ? ENS_MEDIO_QUESTIONNAIRES.PROCESSES : null;
    console.log('[Compliance] Processes radar data calculation:', {
      dataExists: !!data,
      blocksExists: !!blocks,
      formStandard,
      standardUpper,
      isGDPR,
      isSOC2,
      blockStatsExists: !!data?.blockStats,
      answersExists: !!data?.answers,
      blockStatsKeys: data?.blockStats ? Object.keys(data.blockStats) : [],
      blockIds: blocks?.map(b => b.id) || [],
    });

    if (!data || !blocks) {
      console.log('[Compliance] Processes: No data or blocks, returning empty array', { hasData: !!data, hasBlocks: !!blocks, formStandard });
      return [];
    }

    let effectiveBlockStats = data.blockStats;
    if (!effectiveBlockStats || Object.keys(effectiveBlockStats).length === 0) {
      console.log('[Compliance] Processes: blockStats missing or empty, recalculating from answers.');
      effectiveBlockStats = calculateBlockStatsFromAnswers(data.answers || {}, blocks);
    }

    const result = blocks.map((block) => {
      const stats = effectiveBlockStats[block.id] || { yes: 0, no: 0, partial: 0, n_a: 0 };
      const totalQuestionsInBlock = block.questions.length;
      const applicableQuestions = totalQuestionsInBlock - (stats.n_a || 0); // Exclude N/A from denominator
      const score = (stats.yes || 0) * 1 + (stats.partial || 0) * 0.5; // Yes=1, Partial=0.5, No=0
      const percent = applicableQuestions > 0 ? Math.round((score / applicableQuestions) * 100) : 0;
      console.log(`[Compliance] Processes Block ${block.id}: stats=${JSON.stringify(stats)}, totalQuestions=${totalQuestionsInBlock}, percent=${percent}`);
      return {
        subject: locale === 'ru' ? block.name_ru : locale === 'es' ? block.name_es : block.name_en,
        value: percent,
        fullMark: 100,
      };
    });
    console.log('[Compliance] Processes radar result:', result);
    return result;
  }, [extendedQuestionnairesData.PROCESSES, formStandard, dashboardStandard, isAllStandardsSelected, locale, calculateBlockStatsFromAnswers]);

  const rolesRadarData = useMemo(() => {
    const data = extendedQuestionnairesData.ROLES;
    const standardForRadar = (dashboardStandard && dashboardStandard !== 'all' && !isAllStandardsSelected) ? dashboardStandard : formStandard;
            const standardUpper = standardForRadar?.toUpperCase();
            const isGDPR = standardUpper === 'GDPR';
            const isSOC2 = standardUpper === 'SOC 2' || standardUpper === 'SOC2';
            // Support both NIS2 (regular) and NIS2_FULL for full questionnaire
            const isNIS2Full = usesNis2ExtendedSelfAssessment(standardUpper);
            const isENS = standardUpper === 'NATIONAL_ENS_ES' || standardUpper === 'ENS' || standardUpper === 'ENS_ES';
            const isDORA = standardUpper === 'DORA';
            const isISO27001 = standardUpper === 'ISO27001' || standardUpper === 'ISO_27001' || standardUpper === 'ISO 27001' || standardUpper === 'ISO/IEC 27001';
            const blocks = isGDPR ? GDPR_QUESTIONNAIRES.ROLES : 
                           isSOC2 ? SOC2_QUESTIONNAIRES.ROLES :
                           isISO27001 ? ISO27001_QUESTIONNAIRES.ROLES :
                           isNIS2Full ? NIS2_FULL_QUESTIONNAIRES.ROLES :
                           isENS ? null : // ENS doesn't have separate Roles questionnaire
                           isDORA ? DORA_QUESTIONNAIRES.ROLES : isENS ? ENS_MEDIO_QUESTIONNAIRES.ROLES : null;
    console.log('[Compliance] Roles radar data calculation:', {
      dataExists: !!data,
      blocksExists: !!blocks,
      blockStatsExists: !!data?.blockStats,
      answersExists: !!data?.answers,
      blockStatsKeys: data?.blockStats ? Object.keys(data.blockStats) : [],
      blockIds: blocks?.map(b => b.id) || [],
    });

    if (!data || !blocks) {
      console.log('[Compliance] Roles: No data or blocks, returning empty array');
      return [];
    }

    let effectiveBlockStats = data.blockStats;
    if (!effectiveBlockStats || Object.keys(effectiveBlockStats).length === 0) {
      console.log('[Compliance] Roles: blockStats missing or empty, recalculating from answers.');
      effectiveBlockStats = calculateBlockStatsFromAnswers(data.answers || {}, blocks);
    }

    const result = blocks.map((block) => {
      const stats = effectiveBlockStats[block.id] || { yes: 0, no: 0, partial: 0, n_a: 0 };
      const totalQuestionsInBlock = block.questions.length;
      const applicableQuestions = totalQuestionsInBlock - (stats.n_a || 0); // Exclude N/A from denominator
      const score = (stats.yes || 0) * 1 + (stats.partial || 0) * 0.5; // Yes=1, Partial=0.5, No=0
      const percent = applicableQuestions > 0 ? Math.round((score / applicableQuestions) * 100) : 0;
      console.log(`[Compliance] Roles Block ${block.id}: stats=${JSON.stringify(stats)}, totalQuestions=${totalQuestionsInBlock}, percent=${percent}`);
      return {
        subject: locale === 'ru' ? block.name_ru : locale === 'es' ? block.name_es : block.name_en,
        value: percent,
        fullMark: 100,
      };
    });
    console.log('[Compliance] Roles radar result:', result);
    return result;
  }, [extendedQuestionnairesData.ROLES, formStandard, dashboardStandard, isAllStandardsSelected, locale, calculateBlockStatsFromAnswers]);

  const {
    answers,
    setAnswer,
    saveAnswers,
    hydrateAnswers,
    registerCategories,
    isSaving,
    lastSavedAt,
    hasUnsavedChanges,
    isOnline,
  } = useComplianceAnswers(organizationId ?? null, formStandard ?? null, {
    questionnaireType: isIso42001Selection(formStandard) ? "QUICK" : undefined,
    onSurveySaved: () => {
      setDashboardRefreshKey((prev) => prev + 1);
      setFormRefreshKey((prev) => prev + 1);
    },
  });

  const fetchJson = useCallback(
    async <T,>(
      endpoint: string,
      params: Record<string, string | number | null | undefined>,
      signal?: AbortSignal,
    ): Promise<T> => {
      if (!organizationId) {
        throw new Error("Missing organization scope");
      }
      const url = buildUrl(endpoint, {
        organization_id: organizationId,
        ...params,
      });
      const response = await fetch(url, {
        method: "GET",
        headers: authHeaders,
        signal,
      });
      if (!response.ok) {
        const message = `Request failed (${response.status})`;
        throw new Error(message);
      }
      return (await response.json()) as T;
    },
    [authHeaders, organizationId],
  );

  useEffect(() => {
    if (!organizationId) {
      setLoading(false);
      setSummary(null);
      setAnalytics([]);
      setDistribution([]);
      setSurveyRows([]);
      setHistoryRows([]);
      setDashboardCategoryOptions([]);
      return;
    }

    // Only auto-select first standard if user hasn't explicitly selected "All Standards"
    if (availableStandards.length > 0 && !dashboardStandard && !isAllStandardsSelected) {
      setDashboardStandard(availableStandards[0].code);
      return;
    }

    const controller = new AbortController();
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const summaryPayload = await fetchJson<{ success?: boolean; data: SummaryResponse }>(
          API_ENDPOINTS.COMPLIANCE_SUMMARY,
          {
            // Don't pass standard parameter if "All Standards" is selected (empty string or null)
            ...(dashboardStandard && dashboardStandard.trim().length > 0 ? { standard: dashboardStandard } : {}),
            category: dashboardCategory ?? undefined,
          },
          controller.signal,
        );

        const summaryData = summaryPayload?.data ?? null;
        console.log('[Diag] summary', summaryData);
        setSummary(summaryData);

        // Fetch category summary for Category Maturity Map
        const categorySummaryPayload = dashboardStandard && dashboardStandard.trim().length > 0
          ? await fetchJson<{ success?: boolean; data?: Array<{ code: string; name: string; percent: number }> }>(
              API_ENDPOINTS.COMPLIANCE_CATEGORIES_SUMMARY,
              {
                organization_id: organizationId ?? undefined,
                standard: dashboardStandard,
              },
              controller.signal,
            ).catch((err) => {
              if (!isFetchAbortError(err)) {
                console.error('[Compliance] Failed to load category summary:', err);
              }
              return { data: [] };
            })
          : Promise.resolve({ data: [] });

        const [
          analyticsPayload,
          distributionPayload,
          surveyPayload,
          historyPayload,
          dashboardCategoriesPayload,
          auditReadinessPayload,
          driftEventsPayload,
          evidenceRequestsPayload,
          connectorsPayload,
          certificationWorkflowsPayload,
          aiGapAnalysisPayload,
          aiReadinessSummaryPayload,
          aiRemediationPlanPayload,
          aiEvidenceReviewPayload,
          aiPolicyReviewPayload,
        ] = await Promise.all([
          fetchJson<{ success?: boolean; data: AnalyticsPoint[] }>(
            `${API_BASE_URL}/compliance/analytics`,
            {
              organization_id: organizationId ?? undefined,
              // Don't pass standard parameter if "All Standards" is selected (empty string or null)
              ...(dashboardStandard && dashboardStandard.trim().length > 0 ? { standard: dashboardStandard } : {}),
              category: dashboardCategory ?? undefined,
            },
            controller.signal,
          ).catch((err) => {
            if (!isFetchAbortError(err)) {
              console.error('[Compliance] Failed to load analytics:', err);
            }
            return { data: [] };
          }),
          fetchJson<{ success?: boolean; data: DistributionBucket[] }>(
            `${API_BASE_URL}/compliance/distribution`,
            {
              organization_id: organizationId ?? undefined,
              // Don't pass standard parameter if "All Standards" is selected (empty string or null)
              ...(dashboardStandard && dashboardStandard.trim().length > 0 ? { standard: dashboardStandard } : {}),
            },
            controller.signal,
          ).catch((err) => {
            if (!isFetchAbortError(err)) {
              console.error('[Compliance] Failed to load distribution:', err);
            }
            return { data: [] };
          }),
          dashboardStandard && dashboardStandard.trim().length > 0
            ? fetchJson<{ success?: boolean; data?: any[]; scores?: Record<string, any> }>(
                API_ENDPOINTS.COMPLIANCE_SURVEY,
                {
                  standard: dashboardStandard,
                  category: dashboardCategory ?? undefined,
                },
                controller.signal,
              ).catch(() => ({ data: [], scores: null }))
            : Promise.resolve({ data: [], scores: null }),
          fetchJson<{ success?: boolean; data?: any[] }>(
            API_ENDPOINTS.COMPLIANCE_HISTORY,
            {
              // Don't pass standard parameter if "All Standards" is selected (empty string or null)
              ...(dashboardStandard && dashboardStandard.trim().length > 0 ? { standard: dashboardStandard } : {}),
              category: dashboardCategory ?? undefined,
            },
            controller.signal,
          ).catch(() => ({ data: [] })),
          dashboardStandard && dashboardStandard.trim().length > 0
            ? fetchJson<{ success?: boolean; categories?: ComplianceFormCategory[] }>(
                API_ENDPOINTS.COMPLIANCE_CATEGORIES,
                {
                  standard: dashboardStandard,
                },
                controller.signal,
              ).catch(() => ({ categories: [] }))
            : Promise.resolve({ categories: [] }),
          fetchJson<{ success?: boolean; data?: Record<string, unknown> }>(
            API_ENDPOINTS.COMPLIANCE_AUDIT_READINESS,
            {
              ...(dashboardStandard && dashboardStandard.trim().length > 0 ? { standardCode: dashboardStandard } : {}),
            },
            controller.signal,
          ).catch(() => ({ data: null })),
          fetchJson<{ success?: boolean; data?: unknown[] }>(
            API_ENDPOINTS.COMPLIANCE_DRIFT_EVENTS,
            {
              ...(dashboardStandard && dashboardStandard.trim().length > 0 ? { standardCode: dashboardStandard } : {}),
            },
            controller.signal,
          ).catch(() => ({ data: [] })),
          fetchJson<{ success?: boolean; data?: unknown[] }>(
            API_ENDPOINTS.COMPLIANCE_AUTOMATED_EVIDENCE_REQUESTS,
            {
              ...(dashboardStandard && dashboardStandard.trim().length > 0 ? { standardCode: dashboardStandard } : {}),
            },
            controller.signal,
          ).catch(() => ({ data: [] })),
          fetchJson<{ success?: boolean; data?: unknown[] }>(
            API_ENDPOINTS.COMPLIANCE_CONNECTORS,
            {},
            controller.signal,
          ).catch(() => ({ data: [] })),
          fetchJson<{ success?: boolean; data?: Array<Record<string, unknown>> }>(
            API_ENDPOINTS.COMPLIANCE_CERTIFICATION_WORKFLOWS,
            {},
            controller.signal,
          ).catch(() => ({ data: [] })),
          featureFlags.AI_AUDIT_ASSISTANT_V1
            ? fetchJson<{ success?: boolean; data?: Record<string, unknown> }>(
                API_ENDPOINTS.AI_GAP_ANALYSIS,
                {
                  standardCode: dashboardStandard && dashboardStandard.trim().length > 0 ? dashboardStandard : "ALL",
                },
                controller.signal,
              ).catch(() => ({ data: null }))
            : Promise.resolve({ data: null }),
          featureFlags.AI_AUDIT_ASSISTANT_V1
            ? fetchJson<{ success?: boolean; data?: Record<string, unknown> }>(
                API_ENDPOINTS.AI_READINESS_SUMMARY,
                {
                  standardCode: dashboardStandard && dashboardStandard.trim().length > 0 ? dashboardStandard : "ALL",
                },
                controller.signal,
              ).catch(() => ({ data: null }))
            : Promise.resolve({ data: null }),
          featureFlags.AI_AUDIT_ASSISTANT_V1
            ? fetchJson<{ success?: boolean; data?: Record<string, unknown> }>(
                API_ENDPOINTS.AI_REMEDIATION_PLAN,
                {
                  standardCode: dashboardStandard && dashboardStandard.trim().length > 0 ? dashboardStandard : "ALL",
                },
                controller.signal,
              ).catch(() => ({ data: null }))
            : Promise.resolve({ data: null }),
          featureFlags.AI_AUDIT_ASSISTANT_V1
            ? fetchJson<{ success?: boolean; data?: Record<string, unknown> }>(
                API_ENDPOINTS.AI_EVIDENCE_REVIEW,
                {
                  standardCode: dashboardStandard && dashboardStandard.trim().length > 0 ? dashboardStandard : "ALL",
                },
                controller.signal,
              ).catch(() => ({ data: null }))
            : Promise.resolve({ data: null }),
          featureFlags.AI_AUDIT_ASSISTANT_V1
            ? fetchJson<{ success?: boolean; data?: Record<string, unknown> }>(
                API_ENDPOINTS.AI_POLICY_REVIEW,
                {
                  standardCode: dashboardStandard && dashboardStandard.trim().length > 0 ? dashboardStandard : "ALL",
                },
                controller.signal,
              ).catch(() => ({ data: null }))
            : Promise.resolve({ data: null }),
        ]);

        const analyticsData = Array.isArray(analyticsPayload?.data) ? analyticsPayload.data : [];
        const distributionData = Array.isArray(distributionPayload?.data)
          ? distributionPayload.data
          : [];
        
        // Log summary data to check if it has the required fields
        console.log('[Compliance] Summary data:', {
          hasSummary: !!summaryData,
          hasDetails: !!summaryData?.details,
          detailsLength: summaryData?.details?.length || 0,
          hasWeakAreas: !!summaryData?.weakAreas,
          weakAreasLength: summaryData?.weakAreas?.length || 0,
          firstDetail: summaryData?.details?.[0],
          firstWeakArea: summaryData?.weakAreas?.[0],
          summaryKeys: summaryData ? Object.keys(summaryData) : [],
        });
        
        console.log('[Compliance] Analytics data:', {
          payload: analyticsPayload,
          data: analyticsData,
          length: analyticsData.length,
          organizationId,
          dashboardStandard,
          firstPoint: analyticsData[0],
        });
        
        console.log('[Compliance] Distribution data:', {
          payload: distributionPayload,
          data: distributionData,
          length: distributionData.length,
          organizationId,
          dashboardStandard,
          firstBucket: distributionData[0],
        });
        
        setAnalytics(analyticsData);
        setDistribution(distributionData);

        const mappedSurvey = Array.isArray(surveyPayload?.data)
          ? surveyPayload.data.map((item, index) => {
              const control =
                item?.control ??
                item?.question ??
                item?.question_code ??
                item?.name ??
                t("common.unknown", "Unknown");
              const percentSource =
                item?.compliancePercentage ??
                item?.percent ??
                (Number.isFinite(item?.score) ? Number(item.score) * 20 : 0);
              return {
                id: String(item?.id ?? `${control}-${index}`),
                control: String(control),
                standard: String(item?.standard ?? dashboardStandard ?? "—"),
                percent: clampPercent(percentSource),
                category: String(item?.category ?? item?.categoryName ?? "—"),
                lastUpdated:
                  item?.lastUpdated ??
                  item?.last_updated ??
                  item?.updatedAt ??
                  item?.assessmentDate ??
                  null,
                controlsCount: Number.isFinite(item?.controlsCount)
                  ? Number(item.controlsCount)
                  : Number.isFinite(item?.controls_count)
                    ? Number(item.controls_count)
                    : null,
              } as SurveyRow;
            })
          : [];
        setSurveyRows(mappedSurvey);
        
        // Store category breakdown from API response
        const breakdown = surveyPayload?.scores || null;
        setCategoryBreakdown(breakdown);
        
        // Store category summary for Category Maturity Map
        const categorySummary = categorySummaryPayload?.data || [];
        if (categorySummary.length > 0) {
          const breakdownMap: Record<string, any> = {};
          categorySummary.forEach((item) => {
            breakdownMap[item.code] = {
              name: item.name,
              complianceScore: item.percent,
              score: item.percent,
              percent: item.percent
            };
          });
          setCategoryBreakdown(breakdownMap);
        }

        const mappedHistory = Array.isArray(historyPayload?.data)
          ? historyPayload.data.map((item, index) => ({
              id: String(item?.id ?? index),
              date: String(item?.date ?? item?.createdAt ?? new Date().toISOString()),
              standard: String(item?.standard ?? dashboardStandard ?? "—"),
              statusBefore: item?.statusBefore ?? item?.status_before ?? null,
              statusAfter: item?.statusAfter ?? item?.status_after ?? null,
              changedBy: item?.changedBy ?? item?.changed_by ?? null,
              changeReason: item?.changeReason ?? item?.change_reason ?? null,
            }))
          : [];
        setHistoryRows(mappedHistory);

        const dbCategoryOptionsRaw =
          Array.isArray(dashboardCategoriesPayload?.categories) &&
          dashboardCategoriesPayload.categories.length > 0
            ? dashboardCategoriesPayload.categories.map((category) => ({
                value: category.code ?? category.id,
                label: category.name || humanize(category.code ?? category.id),
              }))
            : [];

        const dbCategoryOptions = [
          {
            value: "all",
            label: t("compliance.filters.allCategories", "All Categories"),
          },
          ...dbCategoryOptionsRaw,
        ];
        setDashboardCategoryOptions(dbCategoryOptions);

        if (
          dashboardCategory &&
          !dbCategoryOptions.some((option) => option.value === dashboardCategory)
        ) {
          setDashboardCategory(null);
        }

        const readinessData = auditReadinessPayload?.data || null;
        if (readinessData) {
          const driftEventsCount = Array.isArray(driftEventsPayload?.data) ? driftEventsPayload.data.length : 0;
          const evidenceRequestsCount = Array.isArray(evidenceRequestsPayload?.data) ? evidenceRequestsPayload.data.length : 0;
          const connectorsCount = Array.isArray(connectorsPayload?.data) ? connectorsPayload.data.length : 0;
          const workflows = Array.isArray(certificationWorkflowsPayload?.data) ? certificationWorkflowsPayload.data : [];
          const certificationWorkflowsCount = workflows.length;
          const certificationUnderReviewCount = workflows.filter(
            (workflow) => String(workflow?.status || "").toLowerCase() === "under_review",
          ).length;
          const certificationLatestStatus = certificationWorkflowsCount > 0
            ? String(workflows[0]?.status || "draft")
            : "draft";
          const aiRecommendationCount = Array.isArray((aiGapAnalysisPayload?.data as any)?.nextSteps)
            ? ((aiGapAnalysisPayload?.data as any)?.nextSteps as unknown[]).length
            : 0;
          const aiReadinessNarrative = String((aiReadinessSummaryPayload?.data as any)?.output || "");
          const aiNextStepsCount = Array.isArray((aiRemediationPlanPayload?.data as any)?.planItems)
            ? ((aiRemediationPlanPayload?.data as any)?.planItems as unknown[]).length
            : 0;
          const aiStaleEvidenceCount = Number((aiEvidenceReviewPayload?.data as any)?.staleEvidenceCount || 0);
          const aiMissingEvidenceCount = Number((aiEvidenceReviewPayload?.data as any)?.missingEvidenceCount || 0);
          const aiPolicyOverdueReviewCount = Number((aiPolicyReviewPayload?.data as any)?.overduePolicyReviewCount || 0);
          setAuditReadinessSummary({
            readinessStatus: String(readinessData.readinessStatus || "NOT_READY"),
            readinessScore: Number(readinessData.readinessScore || 0),
            blockersCount: Number(readinessData.blockersCount || 0),
            missingEvidenceCount: Number(readinessData.missingEvidenceCount || 0),
            openCriticalRecommendations: Number(
              readinessData.openCriticalRecommendations || 0,
            ),
            driftEventsCount,
            evidenceRequestsCount,
            connectorsCount,
            certificationWorkflowsCount,
            certificationUnderReviewCount,
            certificationLatestStatus,
            aiRecommendationCount,
            aiReadinessNarrative,
            aiNextStepsCount,
            aiStaleEvidenceCount,
            aiMissingEvidenceCount,
            aiPolicyOverdueReviewCount,
          });
        } else {
          setAuditReadinessSummary(null);
        }

        setLoading(false);
      } catch (err) {
        if (controller.signal.aborted) return;
        console.error("[Compliance] load failed", err);
        setError(
          err instanceof Error
            ? err.message
            : t("compliance.errors.load", "Failed to load compliance data"),
        );
        setLoading(false);
      }
    };

    void load();
    return () => controller.abort();
  }, [
    availableStandards,
    dashboardCategory,
    dashboardRefreshKey,
    dashboardStandard,
    fetchJson,
    organizationId,
    t,
  ]);

  useEffect(() => {
    if (!organizationId || !formStandard) {
      setFormCategories([]);
      registerCategories([]);
      setExpandedCategoryIds([]);
      return;
    }

    const controller = new AbortController();
    const load = async () => {
      try {
        const isoQuick = isIso42001Selection(formStandard);
        if (isoQuick) {
          setIsoQuickLoading(true);
          setIsoQuickError(false);
        }
        const categoriesPayload =
          await fetchJson<{
            success?: boolean;
            categories?: ComplianceFormCategory[];
            definitionAvailable?: boolean;
            questionCount?: number;
            categoryCount?: number;
            answers?: Record<string, number>;
          }>(
            API_ENDPOINTS.COMPLIANCE_CATEGORIES,
            isoQuick
              ? { standard: "ISO42001", questionnaireType: "QUICK" }
              : { standard: formStandard },
            controller.signal,
          );

        console.log('[Compliance] Categories payload:', {
          hasCategories: !!categoriesPayload?.categories,
          categoriesLength: categoriesPayload?.categories?.length || 0,
          firstCategory: categoriesPayload?.categories?.[0],
          firstCategoryQuestions: categoriesPayload?.categories?.[0]?.questions?.length || 0,
          firstQuestion: categoriesPayload?.categories?.[0]?.questions?.[0],
        });

        const localizedCategories = Array.isArray(categoriesPayload?.categories)
          ? categoriesPayload.categories.map((category) => {
              const questions = (category.questions ?? []).map((question) => {
                // Debug: log question data to see what we're working with
                console.log('[Compliance] Processing question:', {
                  id: question.id,
                  code: question.code,
                  text_en: question.text_en,
                  text_es: question.text_es,
                  text_ru: question.text_ru,
                  text: question.text,
                  hasTextEn: !!question.text_en,
                  hasText: !!question.text,
                  hasTextEs: !!question.text_es,
                  hasTextRu: !!question.text_ru,
                });
                
                // Force use text_en if available, regardless of backend's text field
                const finalText = question.text_en || normalizeQuestionLabel(category, question, locale);
                console.log('[Compliance] Final question text:', {
                  questionId: question.id,
                  code: question.code,
                  finalText,
                  originalTextEn: question.text_en,
                  originalText: question.text,
                });
                
                return {
                  ...question,
                  text: finalText,
                  // Always preserve text_en from backend
                  text_en: question.text_en || finalText,
                };
              });
              
              console.log('[Compliance] Category processed:', {
                categoryId: category.id,
                categoryName: category.name,
                questionsCount: questions.length,
                firstQuestion: questions[0],
              });
              
              return {
                ...category,
                questions,
              };
            })
          : [];

        registerCategories(localizedCategories);
        setFormCategories(localizedCategories);
        if (isIso42001Selection(formStandard)) {
          const questionCount = localizedCategories.reduce(
            (sum, category) => sum + (category.questions?.length || 0),
            0,
          );
          const available =
            categoriesPayload?.definitionAvailable === true ||
            (localizedCategories.length === 10 && questionCount === 30);
          setIsoQuickAvailable(available);
          setIsoQuickError(false);
          setIsoQuickLoading(false);
          try {
            const survey = await fetchJson<{ success?: boolean; answers?: Record<string, number> }>(
              API_ENDPOINTS.COMPLIANCE_SURVEY,
              {
                standard: "ISO42001",
                questionnaireType: "QUICK",
                organization_id: organizationId ?? undefined,
              },
              controller.signal,
            );
            hydrateAnswers(groupQuickAnswersByCategory(localizedCategories, survey?.answers || {}));
          } catch (surveyError) {
            if (!controller.signal.aborted) {
              console.error("[Compliance] failed to reload quick answers", surveyError);
            }
          }
        }
        setExpandedCategoryIds((prev) => {
          if (prev.length > 0)
            return prev.filter((id) =>
              localizedCategories.some((category) => category.id === id),
            );
          if (localizedCategories.length > 0)
            return [localizedCategories[0].id];
          return [];
        });
      } catch (error) {
        if (controller.signal.aborted) return;
        console.error("[Compliance] failed to load form categories", error);
        if (isIso42001Selection(formStandard)) {
          setIsoQuickError(true);
          setIsoQuickAvailable(false);
          setIsoQuickLoading(false);
        }
      }
    };

    void load();
    return () => controller.abort();
  }, [
    fetchJson,
    formStandard,
    formRefreshKey,
    iso42001ReloadKey,
    locale,
    organizationId,
    hydrateAnswers,
    registerCategories,
  ]);

  useEffect(() => {
    if (!isIso42001Selection(formStandard)) {
      setIsoFullBlocks([]);
      setIsoFullAvailable(false);
      setIsoFullError(false);
      setIsoFullLoading(false);
      setIsoFullSettled(true);
      return;
    }
    const controller = new AbortController();
    const loadDefinition = async () => {
      setIsoFullSettled(false);
      setIsoFullLoading(true);
      setIsoFullError(false);
      try {
        const payload = await fetchJson<{
          success?: boolean;
          code?: string;
          data?: { available?: boolean; blocks?: ExtendedQuestionnaireBlock[] };
        }>(
          API_ENDPOINTS.COMPLIANCE_QUESTIONNAIRE_DEFINITION,
          { standard: "ISO42001", questionnaireType: "SELF_ASSESSMENT" },
          controller.signal,
        );
        const blocks = Array.isArray(payload?.data?.blocks) ? payload.data.blocks : [];
        const available = payload?.success === true && payload?.data?.available === true && blocks.length > 0;
        setIsoFullBlocks(available ? blocks : []);
        setIsoFullAvailable(available);
        setIsoFullError(false);
      } catch (error) {
        if (controller.signal.aborted) return;
        console.error("[Compliance] failed to load ISO 42001 definition", error);
        setIsoFullBlocks([]);
        setIsoFullAvailable(false);
        setIsoFullError(true);
      } finally {
        if (!controller.signal.aborted) {
          setIsoFullLoading(false);
          setIsoFullSettled(true);
        }
      }
    };
    void loadDefinition();
    return () => controller.abort();
  }, [fetchJson, formStandard, iso42001ReloadKey]);

  // Helper function to normalize standard name for comparison
  const normalizeStandardForFilter = useCallback((standardName: string): string => {
    const upper = standardName.toUpperCase().trim();
    if (upper === "ISO/IEC 42001:2023" || upper === "ISO/IEC 42001" || upper === "ISO 42001" || upper === "ISO42001" || upper === "ISO_42001") return "ISO/IEC 42001:2023";
    if (upper === "ISO/IEC 27001" || upper === "ISO 27001" || upper === "ISO27001" || upper === "ISO_27001") return "ISO 27001";
    if (upper === "PCI DSS" || upper === "PCI-DSS" || upper === "PCI_DSS") return "PCI-DSS";
    if (upper === "PCI DSS 2025" || upper === "PCI_DSS_2025") return "PCI DSS 2025";
    if (upper === "SWIFT CSCF 2025" || upper === "SWIFT_CSCF_2025") return "SWIFT CSCF 2025";
    if (upper === "SOC 2" || upper === "SOC2" || upper === "SOC_2") return "SOC 2";
    if (upper === "GDPR") return "GDPR";
    if (upper === "NIS2" || upper === "NIS 2" || upper === "NIS_2") return "NIS 2";
    if (upper === "DORA") return "DORA";
    if (upper === "SDLC SECURITY" || upper === "SDLC_SECURITY") return "SDLC Security";
    if (upper === "TECHNICAL CONTROLS" || upper === "TECHNICAL_CONTROLS" || upper === "TECH_CONTROLS" || upper === "TECH CONTROLS") return "Technical Controls";
    if (upper === "CIS" || upper.startsWith("CIS CONTROLS") || upper === "CIS_CONTROLS_V8") return "CIS Controls v8";
    // National profiles
    if (upper === "ENS_ES" || upper === "NATIONAL_ENS_ES" || upper === "ENS") return "ENS_ES";
    if (upper === "BSI_DE" || upper === "BSI" || upper === "NATIONAL_BSI_DE") return "BSI_DE";
    if (upper === "ANSSI_FR" || upper === "NATIONAL_ANSSI_FR" || upper === "ANSSI" || upper === "ENS_ANSSI") return "ANSSI_FR";
    if (upper === "KSBP_LT" || upper === "KSBP" || upper === "NATIONAL_KSBP_LT") return "KSBP_LT";
    if (upper === "NCA_EE" || upper === "NCA" || upper === "NATIONAL_NCA_EE") return "NCA_EE";
    return standardName;
  }, []);

  // Helper function to check if a standard is in selected standards
  const isStandardSelected = useCallback((standardName: string): boolean => {
    if (selectedStandards.size === 0) return true; // If no standards selected, show all
    const normalized = normalizeStandardForFilter(standardName);
    return selectedStandards.has(normalized);
  }, [selectedStandards, normalizeStandardForFilter]);

  const totals = useMemo(() => {
    const saData = extendedQuestionnairesData?.SELF_ASSESSMENT;
    const hasExtended = saData?.overallStats && (saData.overallStats.total ?? 0) > 0;
    if (hasExtended && dashboardStandard && !isAllStandardsSelected) {
      const s = saData!.overallStats!;
      const applicable = (s.yes || 0) + (s.no || 0) + (s.partial || 0);
      const totalHealth = applicable > 0 ? Math.round(((s.yes || 0) + (s.partial || 0) * 0.5) / applicable * 100) : 0;
      const readinessStatus = totalHealth >= 80 ? 'COMPLIANT' : totalHealth >= 60 ? 'PARTIAL' : 'NON_COMPLIANT';
      return {
        totalHealth: clampPercent(totalHealth),
        lastUpdated: null,
        totalStandards: 1,
        activeStandards: totalHealth > 0 ? 1 : 0,
        auditStatus: readinessStatus,
        auditPercent: readinessStatus === 'COMPLIANT' ? 100 : readinessStatus === 'PARTIAL' ? 60 : 0,
      };
    }

    const details = summary?.details ?? [];
    
    // Filter details by selected standards if any are selected
    const filteredDetails = selectedStandards.size === 0 
      ? details 
      : details.filter((item) => {
          const code = item.standard ?? item.code ?? '';
          return isStandardSelected(code);
        });
    
    // Deduplicate standards by code to avoid counting duplicates
    const uniqueStandards = new Map<string, { code: string; compliancePercentage: number }>();
    filteredDetails.forEach((item) => {
      const code = item.standard ?? item.code ?? 'Unknown';
      const compliancePercent = item.compliancePercentage ?? item.compliancePercent ?? item.percent ?? item.value ?? 0;
      const clamped = clampPercent(Number(compliancePercent));
      // Keep the entry with the highest compliance percentage if duplicate
      if (!uniqueStandards.has(code) || uniqueStandards.get(code)!.compliancePercentage < clamped) {
        uniqueStandards.set(code, { code, compliancePercentage: clamped });
      }
    });
    
    const uniqueDetails = Array.from(uniqueStandards.values());
    
    // Safe defaults for cards
    const cards = summary?.cards ?? {
      totalHealth: 0,
      activeStandards: 0,
      auditReadiness: 'UNKNOWN',
      lastUpdated: null,
      colorHint: null,
    };
    
    const totalHealth =
      cards.totalHealth != null
        ? clampPercent(cards.totalHealth)
        : uniqueDetails.length > 0
          ? Math.round(
              uniqueDetails.reduce((sum, item) => sum + item.compliancePercentage, 0) /
                uniqueDetails.length,
            )
          : 0;

    const totalStandards = uniqueDetails.length;
    // Always calculate activeStandards from deduplicated data to ensure accuracy
    const activeStandards = uniqueDetails.filter((item) => item.compliancePercentage > 0).length;

    const readinessStatus = (cards.auditReadiness ?? "UNKNOWN").toUpperCase();
    const auditPercent =
      AUDIT_STATUS_WEIGHTS[readinessStatus] ??
      AUDIT_STATUS_WEIGHTS.UNKNOWN;

    const lastUpdatedRaw = cards.lastUpdated ?? null;
    return {
      totalHealth,
      lastUpdated: lastUpdatedRaw,
      totalStandards,
      activeStandards,
      auditStatus: readinessStatus,
      auditPercent,
    };
  }, [summary, selectedStandards, isStandardSelected, dashboardStandard, formStandard, extendedQuestionnairesData?.SELF_ASSESSMENT, isAllStandardsSelected]);

  const recencyPercent = useMemo(() => {
    const lastUpdated = totals?.lastUpdated;
    if (!lastUpdated) return 0;
    const parsed = new Date(lastUpdated);
    if (Number.isNaN(parsed.getTime())) return 0;
    const diffDays = Math.max(
      0,
      Math.round((Date.now() - parsed.getTime()) / (1000 * 60 * 60 * 24)),
    );
    const capped = Math.min(diffDays, 180);
    return clampPercent(100 - (capped / 180) * 100);
  }, [totals]);

  const kpiCards = useMemo(() => {
    const totalStandards = totals?.totalStandards ?? 0;
    const activeStandards = totals?.activeStandards ?? 0;
    const totalHealth = totals?.totalHealth ?? 0;
    const activePercent =
      totalStandards > 0
        ? clampPercent((activeStandards / totalStandards) * 100)
        : 0;

    const auditStatus = totals?.auditStatus ?? "UNKNOWN";
    const auditLabel = humanize(auditStatus);
    const lastFilled =
      totals?.lastUpdated != null
        ? new Date(totals.lastUpdated).toLocaleDateString()
        : t("compliance.kpi.lastUpdatedUnknown", "No data");

    const cards = [
      {
        id: "totalHealth",
        title: embeddedMode
          ? t("compliance.kpi.selfAssessment", "Self-assessment")
          : t("compliance.kpi.totalHealth", "Overall Compliance"),
        primary: embeddedMode ? `${totalHealth}%` : `${totalHealth}% — ${auditLabel}`,
        hint: (summary?.cards ?? {}).colorHint,
        percent: totalHealth,
        subtitle: embeddedMode
          ? t("compliance.kpi.selfAssessmentLastFilled", "Last filled: {date}", { date: lastFilled })
          : t(
              "compliance.kpi.totalHealthHint",
              "Average across assessed standards. Audit readiness: {status}",
              { status: auditLabel },
            ),
      },
      {
        id: "activeStandards",
        title: t("compliance.kpi.activeStandards", "Active Standards"),
        primary: `${activeStandards}/${totalStandards || "—"}`,
        percent: activePercent,
        subtitle: t(
          "compliance.kpi.activeStandardsHint",
          "Currently tracking compliance",
        ),
      },
      {
        id: "lastUpdated",
        title: t("compliance.kpi.lastUpdated", "Last Updated"),
        primary: (totals?.lastUpdated ?? null) ?? t("compliance.kpi.lastUpdatedUnknown", "No data"),
        percent: recencyPercent,
        subtitle: t(
          "compliance.kpi.lastUpdatedHint",
          "Higher is more recent (last 180 days)",
        ),
      },
    ];

    const visible = embeddedMode ? cards.filter((c) => c.id !== "activeStandards") : cards;

    return visible.map((card) => ({
      ...card,
      color: mapThresholdColor(card.percent, card.hint),
    }));
  }, [embeddedMode, recencyPercent, summary, t, totals]);

  const complianceByStandardData = useMemo(() => {
    const details = summary?.details ?? [];
    console.log('[Compliance] Building complianceByStandardData:', {
      hasSummary: !!summary,
      detailsLength: details.length,
      selectedStandardsSize: selectedStandards.size,
      selectedStandards: Array.from(selectedStandards),
      firstDetail: details[0],
      detailsKeys: details[0] ? Object.keys(details[0]) : [],
    });
    
    // Filter details by selected standards if any are selected
    const filteredDetails = selectedStandards.size === 0 
      ? details 
      : details.filter((item) => {
          const code = item.standard ?? item.code ?? '';
          return isStandardSelected(code);
        });
    
    console.log('[Compliance] Filtered details:', {
      originalLength: details.length,
      filteredLength: filteredDetails.length,
    });
    
    // Map and deduplicate by code/standard
    const seen = new Map<string, { code: string; name: string; fullName: string; value: number }>();
    filteredDetails.forEach((item) => {
      // Try different field names for compliance percentage
      const compliancePercent = 
        item.compliancePercentage ?? 
        item.compliancePercent ?? 
        item.percent ?? 
        item.value ?? 
        0;
      // Get standard code/identifier
      const code = item.standard ?? item.code ?? 'Unknown';
      // Get standard name, prefer humanized version but keep it short for display
      const standardName = item.name ?? humanize(code);
      // Shorten long names for better display in charts
      const displayName = standardName.length > 20 
        ? standardName.substring(0, 17) + '...' 
        : standardName;
      const value = clampPercent(Number(compliancePercent));
      // Deduplicate: if we've seen this code, keep the one with higher value (or first if equal)
      if (!seen.has(code) || seen.get(code)!.value < value) {
        seen.set(code, {
          code,
          name: displayName,
          fullName: standardName,
          value,
        });
      }
    });
    // Convert map to array and sort
    return Array.from(seen.values())
      .sort((a, b) => a.value - b.value);
  }, [summary, selectedStandards, isStandardSelected]);

  const radarData = useMemo(
    () => {
      const radar = complianceByStandardData.map((item) => ({
        subject: item.name,
        value: item.value,
      }));
      console.log('[Compliance] Building radarData:', {
        complianceByStandardDataLength: complianceByStandardData.length,
        radarLength: radar.length,
        firstRadarPoint: radar[0],
      });
      return radar;
    },
    [complianceByStandardData],
  );

  const weakAreas = useMemo(() => {
    // Prefer Extended when available (Extended-only source of truth)
    const hasExtendedBlockStats = extendedQuestionnairesData &&
      (extendedQuestionnairesData.SELF_ASSESSMENT?.blockStats && Object.keys(extendedQuestionnairesData.SELF_ASSESSMENT.blockStats).length > 0 ||
       extendedQuestionnairesData.ANNEX_A_CONTROLS?.blockStats && Object.keys(extendedQuestionnairesData.ANNEX_A_CONTROLS.blockStats).length > 0 ||
       extendedQuestionnairesData.GDPR_CONTROLS?.blockStats || extendedQuestionnairesData.DORA_CONTROLS?.blockStats ||
       extendedQuestionnairesData.ENS_CONTROLS?.blockStats || extendedQuestionnairesData.NIS2_CONTROLS?.blockStats ||
       extendedQuestionnairesData.PCI_DSS_2025_CONTROLS?.blockStats || extendedQuestionnairesData.SWIFT_CSCF_2025_CONTROLS?.blockStats);
    const std = dashboardStandard || formStandard;
    if (hasExtendedBlockStats && std && std !== 'all' && !isAllStandardsSelected) {
      const fromExtended = getWeakAreasFromExtended(std, extendedQuestionnairesData as Record<string, { blockStats?: Record<string, { yes: number; no: number; partial: number; n_a: number }> } | null>);
      if (fromExtended.length > 0) {
        return fromExtended.map((w) => ({
          name: w.name.length > 25 ? w.name.substring(0, 22) + '...' : w.name,
          fullName: w.name,
          value: clampPercent(w.percent),
        }));
      }
    }
    // Fallback: summary (legacy short) or complianceByStandardData
    if (Array.isArray(summary?.weakAreas) && summary.weakAreas.length > 0) {
      const filteredWeakAreas = selectedStandards.size === 0 ? summary.weakAreas : summary.weakAreas.filter((item) => isStandardSelected(item.code ?? ''));
      return filteredWeakAreas
        .map((item) => {
          const fullName = item.name ?? humanize(item.code ?? "Unknown");
          return { name: fullName.length > 25 ? fullName.substring(0, 22) + '...' : fullName, fullName, value: clampPercent(item.percent ?? item.value ?? 0) };
        })
        .sort((a, b) => a.value - b.value)
        .slice(0, 5);
    }
    return complianceByStandardData
      .map((item) => ({ name: item.name, fullName: (item as any).fullName || item.name, value: item.value }))
      .sort((a, b) => a.value - b.value)
      .slice(0, 5);
  }, [summary, complianceByStandardData, selectedStandards, isStandardSelected, extendedQuestionnairesData, dashboardStandard, formStandard, isAllStandardsSelected]);

  // Build recommendations from weak areas
  const [complianceRecommendations, setComplianceRecommendations] = useState<Recommendation[]>([]);
  const [recommendationWeakAreaMap, setRecommendationWeakAreaMap] = useState<Record<string, string[]>>({});
  
  // Guard: check if we have compliance data before building recommendations
  const hasWeakAreas = Array.isArray(summary?.weakAreas) && summary.weakAreas.length > 0;
  const hasAnyComplianceData =
    hasWeakAreas ||
    (Array.isArray(complianceByStandardData) && complianceByStandardData.length > 0);

  useEffect(() => {
    if (isIso42001Selection(formStandard) && isIso42001Selection(dashboardStandard)) {
      const answers = (extendedQuestionnairesData?.SELF_ASSESSMENT?.answers || {}) as Record<string, string>;
      const localeKey = locale === "ru" || locale === "es" ? locale : "en";
      const generated = recommendationsFromIso42001Blocks(isoFullBlocks, answers, localeKey).slice(0, 15);
      setComplianceRecommendations(
        generated.map((item) => ({
          id: item.id,
          standard: "ISO27001",
          area: "ORG_GOV",
          priority: item.priority,
          titleKey: item.id,
          displayTitle: item.title,
          bodyKey: item.id,
          actionsKeys: [],
          evidenceKeys: [],
          tags: [item.domainKey, item.controlCode || ""].filter(Boolean),
        })) as Recommendation[],
      );
      setRecommendationWeakAreaMap({});
      return;
    }
    if (!dashboardStandard || dashboardStandard === 'all' || isAllStandardsSelected) {
      setComplianceRecommendations([]);
      return;
    }
    // Allow loading recos from extended questionnaire data even when no summary weak areas
    const hasExtendedData = !!(
      extendedQuestionnairesData?.SELF_ASSESSMENT?.answers && Object.keys(extendedQuestionnairesData.SELF_ASSESSMENT.answers).length > 0
    ) || !!(
      extendedQuestionnairesData?.ANNEX_A_CONTROLS?.answers && Object.keys(extendedQuestionnairesData.ANNEX_A_CONTROLS.answers).length > 0
    ) || !!(
      extendedQuestionnairesData?.GDPR_CONTROLS?.answers && Object.keys(extendedQuestionnairesData.GDPR_CONTROLS.answers).length > 0
    ) || !!(
      extendedQuestionnairesData?.DORA_CONTROLS?.answers && Object.keys(extendedQuestionnairesData.DORA_CONTROLS.answers).length > 0
    ) || !!(
      extendedQuestionnairesData?.ENS_CONTROLS?.answers && Object.keys(extendedQuestionnairesData.ENS_CONTROLS.answers).length > 0
    ) || !!(
      extendedQuestionnairesData?.SOC2_CONTROLS?.answers && Object.keys(extendedQuestionnairesData.SOC2_CONTROLS.answers).length > 0
    ) || !!(
      extendedQuestionnairesData?.NIS2_CONTROLS?.answers && Object.keys(extendedQuestionnairesData.NIS2_CONTROLS.answers).length > 0
    ) || !!(
      extendedQuestionnairesData?.PCI_DSS_2025_CONTROLS?.answers && Object.keys(extendedQuestionnairesData.PCI_DSS_2025_CONTROLS.answers).length > 0
    ) || !!(
      extendedQuestionnairesData?.SWIFT_CSCF_2025_CONTROLS?.answers && Object.keys(extendedQuestionnairesData.SWIFT_CSCF_2025_CONTROLS.answers).length > 0
    );
    if (!hasAnyComplianceData && !hasExtendedData) {
      setComplianceRecommendations([]);
      return;
    }
    // Use Extended weak areas when available; legacy fallback to summary (short-derived)
    const hasExtended = !!(
      extendedQuestionnairesData?.SELF_ASSESSMENT?.blockStats && Object.keys(extendedQuestionnairesData.SELF_ASSESSMENT.blockStats).length > 0
    ) || !!(
      extendedQuestionnairesData?.ANNEX_A_CONTROLS?.blockStats && Object.keys(extendedQuestionnairesData.ANNEX_A_CONTROLS.blockStats).length > 0
    ) || !!(
      extendedQuestionnairesData?.GDPR_CONTROLS?.blockStats || extendedQuestionnairesData?.DORA_CONTROLS?.blockStats ||
      extendedQuestionnairesData?.ENS_CONTROLS?.blockStats || extendedQuestionnairesData?.NIS2_CONTROLS?.blockStats ||
      extendedQuestionnairesData?.PCI_DSS_2025_CONTROLS?.blockStats || extendedQuestionnairesData?.SWIFT_CSCF_2025_CONTROLS?.blockStats
    );
    const weakAreasList = hasExtended && extendedQuestionnairesData
      ? getWeakAreasFromExtended(dashboardStandard || '', extendedQuestionnairesData as Record<string, { blockStats?: Record<string, { yes: number; no: number; partial: number; n_a: number }> } | null>).map((w) => ({ code: w.code, name: w.name, percent: w.percent }))
      : (Array.isArray(summary?.weakAreas) ? summary.weakAreas : []);
    const safeCards = summary?.cards ?? {};
    const answeredVal = safeCards.activeStandards ?? 0;
    const totalVal = Math.max(1, answeredVal, 5);
    const answeredStats = { answered: answeredVal, total: totalVal };

    const loadRecos = async () => {
      try {
        const mod = await importWithReload(() => import('../utils/recommendationsCatalog'));
        let list: Recommendation[] = [];
        let weakAreaMap: Record<string, string[]> = {};

        const stdUpper = (dashboardStandard || '').toUpperCase();
        const isISO27 = stdUpper.includes('ISO') && stdUpper.includes('27001');

        // Legacy fallback: only use buildComplianceRecommendations (short-derived) when Extended is missing
        const baseRecos = !hasExtended && mod?.buildComplianceRecommendations && weakAreasList.length > 0
          ? await mod.buildComplianceRecommendations({
              standardCode: dashboardStandard,
              weakAreas: weakAreasList,
              score: safeCards.totalHealth ?? 0,
              answeredStats,
            })
          : [];
        list = Array.isArray(baseRecos) ? baseRecos : [];

        if (dashboardStandard && weakAreasList.length > 0) {
          const mappingModule = await importWithReload(() => import('../utils/recommendationsMapping'));
          list.forEach((reco) => {
            const triggeredBy: string[] = [];
            weakAreasList.forEach((wa) => {
              const waName = wa?.name ?? wa?.code ?? '';
              if (waName) {
                const recIds = mappingModule.getRecommendationIdsForWeakArea(dashboardStandard as import('../utils/recommendationsTypes').StandardCode, waName);
                if (Array.isArray(recIds) && recIds.includes(reco.id)) triggeredBy.push(waName);
              }
            });
            if (triggeredBy.length > 0) weakAreaMap[reco.id] = triggeredBy;
          });
        }

        if (isISO27 && mod?.buildIso27001RecommendationsFromExtendedData) {
          const annexData = extendedQuestionnairesData?.ANNEX_A_CONTROLS?.answers;
          const selfData = extendedQuestionnairesData?.SELF_ASSESSMENT?.answers;
          const { recommendations: isoRecos, triggeredBy: isoTriggered } = await mod.buildIso27001RecommendationsFromExtendedData({
            selfAssessmentAnswers: selfData as Record<string, string> | undefined,
            annexAAnswers: annexData as Record<string, string> | undefined,
          });
          const seen = new Set(list.map((r) => r.id));
          for (const r of isoRecos) {
            if (!seen.has(r.id)) {
              seen.add(r.id);
              list.push(r);
            }
            if (isoTriggered[r.id]) {
              weakAreaMap[r.id] = [...(weakAreaMap[r.id] || []), ...isoTriggered[r.id]];
            }
          }
        }

        const isGDPR = stdUpper === 'GDPR';
        const isDora = stdUpper === 'DORA';
        const isEns = stdUpper === 'ENS' || stdUpper === 'ENS_ES' || stdUpper === 'NATIONAL_ENS_ES';
        const isSoc2 = stdUpper === 'SOC 2' || stdUpper === 'SOC2';
        const isNis2 = usesNis2ExtendedSelfAssessment(stdUpper);
        if (isGDPR && mod?.buildGdprRecommendationsFromExtendedData) {
          const selfData = extendedQuestionnairesData?.SELF_ASSESSMENT?.answers;
          const ctrlData = extendedQuestionnairesData?.GDPR_CONTROLS?.answers;
          const { recommendations: gdprRecos, triggeredBy: gdprTriggered } = await mod.buildGdprRecommendationsFromExtendedData({
            selfAssessmentAnswers: selfData as Record<string, string> | undefined,
            controlsAnswers: ctrlData as Record<string, string> | undefined,
          });
          const seen = new Set(list.map((r) => r.id));
          for (const r of gdprRecos) {
            if (!seen.has(r.id)) {
              seen.add(r.id);
              list.push(r);
            }
            if (gdprTriggered[r.id]) {
              weakAreaMap[r.id] = [...(weakAreaMap[r.id] || []), ...gdprTriggered[r.id]];
            }
          }
        }

        if (isDora && mod?.buildDoraRecommendationsFromExtendedData) {
          const selfData = extendedQuestionnairesData?.SELF_ASSESSMENT?.answers;
          const ctrlData = extendedQuestionnairesData?.DORA_CONTROLS?.answers;
          const { recommendations: doraRecos, triggeredBy: doraTriggered } = await mod.buildDoraRecommendationsFromExtendedData({
            selfAssessmentAnswers: selfData as Record<string, string> | undefined,
            controlsAnswers: ctrlData as Record<string, string> | undefined,
          });
          const seen = new Set(list.map((r) => r.id));
          for (const r of doraRecos) {
            if (!seen.has(r.id)) {
              seen.add(r.id);
              list.push(r);
            }
            if (doraTriggered[r.id]) {
              weakAreaMap[r.id] = [...(weakAreaMap[r.id] || []), ...doraTriggered[r.id]];
            }
          }
        }

        if (isEns && mod?.buildEnsRecommendationsFromExtendedData) {
          const selfData = extendedQuestionnairesData?.SELF_ASSESSMENT?.answers;
          const ctrlData = extendedQuestionnairesData?.ENS_CONTROLS?.answers;
          const { recommendations: ensRecos, triggeredBy: ensTriggered } = await mod.buildEnsRecommendationsFromExtendedData({
            selfAssessmentAnswers: selfData as Record<string, string> | undefined,
            controlsAnswers: ctrlData as Record<string, string> | undefined,
          });
          const seen = new Set(list.map((r) => r.id));
          for (const r of ensRecos) {
            if (!seen.has(r.id)) {
              seen.add(r.id);
              list.push(r);
            }
            if (ensTriggered[r.id]) {
              weakAreaMap[r.id] = [...(weakAreaMap[r.id] || []), ...ensTriggered[r.id]];
            }
          }
        }

        if (isSoc2 && mod?.buildSoc2RecommendationsFromExtendedData) {
          const selfData = extendedQuestionnairesData?.SELF_ASSESSMENT?.answers;
          const { recommendations: soc2Recos, triggeredBy: soc2Triggered } = await mod.buildSoc2RecommendationsFromExtendedData({
            selfAssessmentAnswers: selfData as Record<string, string> | undefined,
            controlsAnswers: {},
          });
          const seen = new Set(list.map((r) => r.id));
          for (const r of soc2Recos) {
            if (!seen.has(r.id)) {
              seen.add(r.id);
              list.push(r);
            }
            if (soc2Triggered[r.id]) {
              weakAreaMap[r.id] = [...(weakAreaMap[r.id] || []), ...soc2Triggered[r.id]];
            }
          }
        }

        if (isNis2 && mod?.buildNis2RecommendationsFromExtendedData) {
          const selfData = extendedQuestionnairesData?.SELF_ASSESSMENT?.answers;
          const ctrlData = extendedQuestionnairesData?.NIS2_CONTROLS?.answers;
          const { recommendations: nis2Recos, triggeredBy: nis2Triggered } = await mod.buildNis2RecommendationsFromExtendedData({
            selfAssessmentAnswers: selfData as Record<string, string> | undefined,
            controlsAnswers: ctrlData as Record<string, string> | undefined,
          });
          const seen = new Set(list.map((r) => r.id));
          for (const r of nis2Recos) {
            if (!seen.has(r.id)) {
              seen.add(r.id);
              list.push(r);
            }
            if (nis2Triggered[r.id]) {
              weakAreaMap[r.id] = [...(weakAreaMap[r.id] || []), ...nis2Triggered[r.id]];
            }
          }
        }

        const isPciDss = stdUpper === 'PCI DSS 2025' || stdUpper === 'PCI_DSS_2025';
        if (isPciDss && mod?.buildPciDssRecommendationsFromExtendedData) {
          const selfData = extendedQuestionnairesData?.SELF_ASSESSMENT?.answers;
          const ctrlData = extendedQuestionnairesData?.PCI_DSS_2025_CONTROLS?.answers;
          const { recommendations: pciRecos, triggeredBy: pciTriggered } = await mod.buildPciDssRecommendationsFromExtendedData({
            selfAssessmentAnswers: selfData as Record<string, string> | undefined,
            controlsAnswers: ctrlData as Record<string, string> | undefined,
          });
          const seen = new Set(list.map((r) => r.id));
          for (const r of pciRecos) {
            if (!seen.has(r.id)) {
              seen.add(r.id);
              list.push(r);
            }
            if (pciTriggered[r.id]) {
              weakAreaMap[r.id] = [...(weakAreaMap[r.id] || []), ...pciTriggered[r.id]];
            }
          }
        }

        const isSwiftCscf = stdUpper === 'SWIFT CSCF 2025' || stdUpper === 'SWIFT_CSCF_2025';
        if (isSwiftCscf && mod?.buildSwiftCscfRecommendationsFromExtendedData) {
          const selfData = extendedQuestionnairesData?.SELF_ASSESSMENT?.answers;
          const ctrlData = extendedQuestionnairesData?.SWIFT_CSCF_2025_CONTROLS?.answers;
          const { recommendations: swiftRecos, triggeredBy: swiftTriggered } = await mod.buildSwiftCscfRecommendationsFromExtendedData({
            selfAssessmentAnswers: selfData as Record<string, string> | undefined,
            controlsAnswers: ctrlData as Record<string, string> | undefined,
          });
          const seen = new Set(list.map((r) => r.id));
          for (const r of swiftRecos) {
            if (!seen.has(r.id)) {
              seen.add(r.id);
              list.push(r);
            }
            if (swiftTriggered[r.id]) {
              weakAreaMap[r.id] = [...(weakAreaMap[r.id] || []), ...swiftTriggered[r.id]];
            }
          }
        }

        const isSdlcSecurity = stdUpper === 'SDLC SECURITY' || stdUpper === 'SDLC_SECURITY';
        if (isSdlcSecurity && mod?.buildSdlcRecommendationsFromExtendedData) {
          const selfData = extendedQuestionnairesData?.SELF_ASSESSMENT?.answers;
          const { recommendations: sdlcRecos, triggeredBy: sdlcTriggered } = await mod.buildSdlcRecommendationsFromExtendedData({
            selfAssessmentAnswers: selfData as Record<string, string> | undefined,
          });
          const seen = new Set(list.map((r) => r.id));
          for (const r of sdlcRecos) {
            if (!seen.has(r.id)) {
              seen.add(r.id);
              list.push(r);
            }
            if (sdlcTriggered[r.id]) {
              weakAreaMap[r.id] = [...(weakAreaMap[r.id] || []), ...sdlcTriggered[r.id]];
            }
          }
        }

        const isTechControls = stdUpper === 'TECHNICAL CONTROLS' || stdUpper === 'TECHNICAL_CONTROLS' || stdUpper === 'TECH_CONTROLS' || stdUpper === 'TECH CONTROLS';
        if (isTechControls && mod?.buildTechnicalControlsRecommendationsFromExtendedData) {
          const selfData = extendedQuestionnairesData?.SELF_ASSESSMENT?.answers;
          const { recommendations: techRecos, triggeredBy: techTriggered } = await mod.buildTechnicalControlsRecommendationsFromExtendedData({
            selfAssessmentAnswers: selfData as Record<string, string> | undefined,
          });
          const seen = new Set(list.map((r) => r.id));
          for (const r of techRecos) {
            if (!seen.has(r.id)) {
              seen.add(r.id);
              list.push(r);
            }
            if (techTriggered[r.id]) {
              weakAreaMap[r.id] = [...(weakAreaMap[r.id] || []), ...techTriggered[r.id]];
            }
          }
        }

        const nationalProfiles = ['BSI_DE', 'ANSSI_FR', 'KSBP_LT', 'NCA_EE'];
        if (nationalProfiles.includes(stdUpper) && mod?.buildNationalProfileRecommendationsFromExtendedData) {
          const selfData = extendedQuestionnairesData?.SELF_ASSESSMENT?.answers;
          const { recommendations: npRecos, triggeredBy: npTriggered } = await mod.buildNationalProfileRecommendationsFromExtendedData({
            standardCode: stdUpper,
            selfAssessmentAnswers: selfData as Record<string, string> | undefined,
          });
          const seen = new Set(list.map((r) => r.id));
          for (const r of npRecos) {
            if (!seen.has(r.id)) {
              seen.add(r.id);
              list.push(r);
            }
            if (npTriggered[r.id]) {
              weakAreaMap[r.id] = [...(weakAreaMap[r.id] || []), ...npTriggered[r.id]];
            }
          }
        }

        setComplianceRecommendations(list.slice(0, 15));
        setRecommendationWeakAreaMap(weakAreaMap);
      } catch (err) {
        console.error('[Compliance] Error loading recommendations:', err);
        setComplianceRecommendations([]);
        setRecommendationWeakAreaMap({});
      }
    };
    queueMicrotask(loadRecos);
  }, [hasAnyComplianceData, dashboardStandard, formStandard, isAllStandardsSelected, summary, complianceByStandardData, extendedQuestionnairesData, isoFullBlocks, locale]);

  // Category radar data for selected standard
  const categoryRadarData = useMemo(() => {
    if (!dashboardStandard || dashboardStandard === 'all' || isAllStandardsSelected) {
      return []
    }

    const stdU = (dashboardStandard || '').toUpperCase();
    const saData = extendedQuestionnairesData?.SELF_ASSESSMENT;
    const hasExtended = saData?.blockStats && Object.keys(saData.blockStats).length > 0;
    const blockStats = hasExtended ? (saData!.blockStats || {}) : {};

    let blocks: Array<{ id: string; name_en?: string; name_es?: string; name_ru?: string; questions: unknown[] }> | null = null;
    if (stdU === 'SOC 2' || stdU === 'SOC2') blocks = SOC2_QUESTIONNAIRES?.SELF_ASSESSMENT ?? null;
    else if (stdU === 'GDPR') blocks = GDPR_QUESTIONNAIRES?.SELF_ASSESSMENT ?? null;
    else if (stdU === 'DORA') blocks = DORA_QUESTIONNAIRES?.SELF_ASSESSMENT ?? null;
    else if (stdU === 'NATIONAL_ENS_ES' || stdU === 'ENS' || stdU === 'ENS_ES') blocks = ENS_MEDIO_QUESTIONNAIRES?.SELF_ASSESSMENT ?? null;
    else if (usesNis2ExtendedSelfAssessment(stdU)) blocks = NIS2_FULL_QUESTIONNAIRES?.SELF_ASSESSMENT ?? null;
    else if (stdU === 'ISO27001' || stdU === 'ISO 27001' || stdU === 'ISO/IEC 27001' || stdU === 'ISO_27001') blocks = ISO27001_QUESTIONNAIRES?.SELF_ASSESSMENT ?? null;
    else if (stdU === 'PCI DSS 2025' || stdU === 'PCI_DSS_2025') blocks = PCI_DSS_2025_SELF_ASSESSMENT_BLOCKS ?? null;
    else if (stdU === 'SWIFT CSCF 2025' || stdU === 'SWIFT_CSCF_2025') blocks = SWIFT_CSCF_2025_SELF_ASSESSMENT_BLOCKS ?? null;
    else if (stdU === 'SDLC SECURITY' || stdU === 'SDLC_SECURITY') blocks = SDLC_SECURITY_SELF_ASSESSMENT_BLOCKS ?? null;
    else if (stdU === 'TECHNICAL CONTROLS' || stdU === 'TECHNICAL_CONTROLS' || stdU === 'TECH_CONTROLS') blocks = TECHNICAL_CONTROLS_SELF_ASSESSMENT_BLOCKS ?? null;

    if (hasExtended && blocks && blocks.length > 0) {
      return blocks.map((block) => {
        const stats = blockStats[block.id] || { yes: 0, no: 0, partial: 0, n_a: 0 };
        const totalQuestions = (block.questions?.length ?? 0) || 1;
        const applicableQuestions = totalQuestions - (stats.n_a || 0);
        const score = (stats.yes || 0) * 1 + (stats.partial || 0) * 0.5;
        const percent = applicableQuestions > 0 ? Math.round((score / applicableQuestions) * 100) : 0;
        const subject = locale === 'ru' ? block.name_ru : locale === 'es' ? block.name_es : block.name_en;
        const label = subject || block.id?.replace(/_/g, ' ') || block.id;
        return { subject: label?.length > 15 ? label.substring(0, 12) + '...' : label, fullName: label, value: clampPercent(percent) };
      });
    }

    // Get categories for the selected standard
    const categoryScores = new Map<string, { name: string; scores: number[] }>()
    
    // First, initialize with dashboardCategoryOptions to ensure all categories are included
    if (dashboardCategoryOptions.length > 0) {
      dashboardCategoryOptions.forEach(cat => {
        if (cat.value !== 'all' && !categoryScores.has(cat.value)) {
          categoryScores.set(cat.value, {
            name: cat.label,
            scores: []
          })
        }
      })
    }
    
    // Filter surveyRows by selected standard (try multiple matching strategies)
    const selectedStandardLower = String(dashboardStandard ?? '').toLowerCase()
    const standardSurveyRows = surveyRows.filter(row => {
      const rowStandard = String(row.standard ?? '').toLowerCase()
      // Try exact match
      if (rowStandard === selectedStandardLower) return true
      // Try partial match
      if (rowStandard.includes(selectedStandardLower) || selectedStandardLower.includes(rowStandard)) return true
      // Try with underscores/dashes
      const normalizedRow = rowStandard.replace(/[_-]/g, '').replace(/\s+/g, '')
      const normalizedSelected = selectedStandardLower.replace(/[_-]/g, '').replace(/\s+/g, '')
      if (normalizedRow === normalizedSelected) return true
      // Try matching with ISO_27001 vs ISO27001, DORA vs DORA_REGULATION, etc.
      if (normalizedRow.includes(normalizedSelected) || normalizedSelected.includes(normalizedRow)) return true
      return false
    })
    
    console.log('[Compliance] Category Maturity Map data:', {
      dashboardStandard,
      surveyRowsLength: surveyRows.length,
      standardSurveyRowsLength: standardSurveyRows.length,
      firstRow: standardSurveyRows[0],
      dashboardCategoryOptionsLength: dashboardCategoryOptions.length,
      sampleCategories: standardSurveyRows.slice(0, 3).map(r => r.category),
      summaryDetailsLength: summary?.details?.length || 0
    })
    
    // Group by category and collect all scores from surveyRows
    standardSurveyRows.forEach(row => {
      const categoryName = String(row.category ?? '').trim()
      if (categoryName && categoryName !== '—' && categoryName !== '-' && categoryName !== '') {
        // Try to match with existing categories (by value or label)
        let matchedCategory = null
        for (const [key, cat] of categoryScores.entries()) {
          const catNameLower = cat.name.toLowerCase()
          const catValueLower = key.toLowerCase()
          const rowCategoryLower = categoryName.toLowerCase()
          if (catNameLower === rowCategoryLower || 
              catValueLower === rowCategoryLower ||
              catNameLower.includes(rowCategoryLower) ||
              rowCategoryLower.includes(catNameLower)) {
            matchedCategory = key
            break
          }
        }
        
        // If no match found, add as new category
        const categoryKey = matchedCategory || categoryName
        const existing = categoryScores.get(categoryKey)
        const score = clampPercent(row.percent ?? 0)
        if (existing) {
          existing.scores.push(score)
        } else {
          categoryScores.set(categoryKey, {
            name: categoryName,
            scores: [score]
          })
        }
      }
    })
    
    // Priority: Use categorySummary from API (already in 0-100 percentage)
    // This is the most reliable source for category maturity data
    if (categoryBreakdown && typeof categoryBreakdown === 'object') {
      Object.entries(categoryBreakdown).forEach(([categoryKey, categoryData]: [string, any]) => {
        if (categoryData && typeof categoryData === 'object') {
          const categoryName = categoryData.name || categoryData.label || humanize(categoryKey);
          // categorySummary data is already in 0-100 percentage (from API)
          // categoryBreakdown may have percent, complianceScore, score, or value
          let complianceScore = categoryData.percent || categoryData.complianceScore || categoryData.score || categoryData.value || 0;
          // If score is in 0-5 scale (less than 6), convert to percentage
          if (complianceScore < 6) {
            complianceScore = complianceScore * 20; // Convert from 0-5 scale to 0-100
          }
          const score = clampPercent(Number(complianceScore));
          
          // Try to match with existing categories
          let matchedCategory = null;
          for (const [key, cat] of categoryScores.entries()) {
            const catNameLower = cat.name.toLowerCase();
            const catValueLower = key.toLowerCase();
            const rowCategoryLower = categoryName.toLowerCase();
            if (catNameLower === rowCategoryLower || 
                catValueLower === rowCategoryLower ||
                catNameLower.includes(rowCategoryLower) ||
                rowCategoryLower.includes(catNameLower)) {
              matchedCategory = key;
              break;
            }
          }
          
          const categoryKeyFinal = matchedCategory || categoryKey;
          const existing = categoryScores.get(categoryKeyFinal);
          if (existing) {
            // Replace scores with new value (categorySummary is authoritative)
            categoryScores.set(categoryKeyFinal, {
              name: categoryName,
              scores: [score]
            });
          } else {
            categoryScores.set(categoryKeyFinal, {
              name: categoryName,
              scores: [score]
            });
          }
        }
      });
    }
    
    // If still no data, try to use summary.details as fallback
    if (standardSurveyRows.length === 0 && !categoryBreakdown && summary?.details && Array.isArray(summary.details)) {
      const selectedStandardCode = String(dashboardStandard ?? '').toUpperCase()
      summary.details.forEach((detail: any) => {
        const detailStandard = String(detail.standard ?? detail.code ?? '').toUpperCase()
        // Try to match standard
        if (detailStandard === selectedStandardCode || 
            detailStandard.includes(selectedStandardCode) || 
            selectedStandardCode.includes(detailStandard)) {
          // Try to extract category from detail
          const categoryName = String(detail.category ?? detail.categoryName ?? '').trim()
          if (categoryName && categoryName !== '—' && categoryName !== '-' && categoryName !== '') {
            const compliancePercent = detail.compliancePercentage ?? detail.compliancePercent ?? detail.percent ?? detail.value ?? 0
            const score = clampPercent(Number(compliancePercent))
            
            // Try to match with existing categories
            let matchedCategory = null
            for (const [key, cat] of categoryScores.entries()) {
              const catNameLower = cat.name.toLowerCase()
              const catValueLower = key.toLowerCase()
              const rowCategoryLower = categoryName.toLowerCase()
              if (catNameLower === rowCategoryLower || 
                  catValueLower === rowCategoryLower ||
                  catNameLower.includes(rowCategoryLower) ||
                  rowCategoryLower.includes(catNameLower)) {
                matchedCategory = key
                break
              }
            }
            
            const categoryKey = matchedCategory || categoryName
            const existing = categoryScores.get(categoryKey)
            if (existing) {
              existing.scores.push(score)
            } else {
              categoryScores.set(categoryKey, {
                name: categoryName,
                scores: [score]
              })
            }
          }
        }
      })
    }
    
    // Calculate average score for each category
    // If categorySummary data was used (scores.length === 1), use that value directly
    // Otherwise, calculate average from multiple scores
    const categoryData = Array.from(categoryScores.values())
      .filter(item => item.scores.length > 0 || dashboardCategoryOptions.some(opt => opt.label === item.name || opt.value === item.name)) // Only include categories with scores or from dashboardCategoryOptions
      .map(item => {
        let avgScore = 0
        if (item.scores.length === 1) {
          // Single score from categorySummary API - use directly (already in 0-100 percentage)
          avgScore = item.scores[0]
        } else if (item.scores.length > 1) {
          // Multiple scores - calculate average
          avgScore = Math.round(item.scores.reduce((sum, s) => sum + s, 0) / item.scores.length)
        }
        
        return {
          subject: item.name.length > 15 ? item.name.substring(0, 12) + '...' : item.name,
          fullName: item.name,
          value: clampPercent(avgScore)
        }
      })
    
    const result = categoryData.sort((a, b) => a.value - b.value)
    console.log('[Compliance] Category radar data result:', {
      resultLength: result.length,
      firstItem: result[0],
      allItems: result.map(r => ({ name: r.fullName, value: r.value })),
      categoryScoresSize: categoryScores.size,
      hasCategoryBreakdown: !!categoryBreakdown,
      categoryBreakdownKeys: categoryBreakdown ? Object.keys(categoryBreakdown) : []
    })
    
    return result
  }, [dashboardStandard, surveyRows, dashboardCategoryOptions, isAllStandardsSelected, summary, categoryBreakdown, extendedQuestionnairesData?.SELF_ASSESSMENT, locale])

  const analyticsChartData = useMemo(
    () => {
      const saData = extendedQuestionnairesData?.SELF_ASSESSMENT;
      const chartData = buildTrendChartData({
        analytics,
        overallStats: saData?.overallStats,
        isAllStandardsSelected,
        hasDashboardStandard: !!dashboardStandard,
      });
      return chartData.map((row: { label: string; value: number }) => ({
        ...row,
        value: clampPercent(Number(row.value || 0)),
      }));
    },
    [analytics, dashboardStandard, extendedQuestionnairesData?.SELF_ASSESSMENT, isAllStandardsSelected],
  );

  const distributionChartData = useMemo(
    () => {
      const saData = extendedQuestionnairesData?.SELF_ASSESSMENT;
      return buildDistributionChartData({
        distribution,
        overallStats: saData?.overallStats,
        isAllStandardsSelected,
        hasDashboardStandard: !!dashboardStandard,
      });
    },
    [distribution, dashboardStandard, extendedQuestionnairesData?.SELF_ASSESSMENT, isAllStandardsSelected],
  );

  const handleDashboardStandardChange = useCallback(
    (value: string | null) => {
      const normalized = value && value.trim().length > 0 ? value.trim() : null;
      const isAllStandards = !normalized || normalized === '';
      setIsAllStandardsSelected(isAllStandards);
      if (normalized === dashboardStandard && !isAllStandards) return;
      setDashboardStandard(isAllStandards ? null : normalized);
      setDashboardCategory(null);
      setDashboardRefreshKey((prev) => prev + 1);
    },
    [dashboardStandard],
  );

  const handleDashboardCategoryChange = useCallback(
    (value: string) => {
      const normalized = value === "all" ? null : value;
      if (normalized === dashboardCategory) return;
      setDashboardCategory(normalized);
      setDashboardRefreshKey((prev) => prev + 1);
    },
    [dashboardCategory],
  );

  const handleFormStandardChange = useCallback(
    (value: string | null) => {
      const normalized = value && value.trim().length > 0 ? value : null;
      if (normalized === formStandard) return;
      setFormStandard(normalized);
      setFormCategoryCode(null);
      setQuestionnaireType('SELF_ASSESSMENT');
      setFormRefreshKey((prev) => prev + 1);
    },
    [formStandard],
  );

  const handleFormCategoryChange = useCallback(
    (value: string) => {
      const normalized = value === "all" ? null : value;
      if (normalized === formCategoryCode) return;
      setFormCategoryCode(normalized);
    },
    [formCategoryCode],
  );

  const validateReportUploadContext = useCallback((): boolean => {
    setImportFeedback(null);
    if (!organizationId) {
      setImportFeedback({
        type: "error",
        message: t(
          "compliance.forms.selectOrganization",
          "Select an organization before uploading a report.",
        ),
      });
      return false;
    }
    if (!formStandard) {
      setImportFeedback({
        type: "error",
        message: t(
          "compliance.forms.selectStandardFirst",
          "Select a standard above to begin.",
        ),
      });
      return false;
    }
    return true;
  }, [organizationId, formStandard, t]);

  const handleComplianceReportPicked = useCallback(
    async (file: File | null) => {
      if (!file) {
        setSelectedReportFile(null);
        return;
      }
      setSelectedReportFile(file);

      if (!organizationId) {
        setImportFeedback({
          type: "error",
          message: t(
            "compliance.forms.selectOrganization",
            "Select an organization before uploading a report.",
          ),
        });
        setSelectedReportFile(null);
        return;
      }
      if (!formStandard) {
        setImportFeedback({
          type: "error",
          message: t(
            "compliance.forms.selectStandardFirst",
            "Select a standard above to begin.",
          ),
        });
        setSelectedReportFile(null);
        return;
      }

      setIsUploadingReport(true);
      setImportFeedback(null);
      try {
        await apiService.uploadComplianceReport({
          file,
          organizationId,
          standardCode: formStandard,
        });

        setImportFeedback({
          type: "success",
          message: t(
            "compliance.forms.importSuccess",
            "Report imported successfully.",
          ),
        });
        setDashboardRefreshKey((prev) => prev + 1);
        setFormRefreshKey((prev) => prev + 1);
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : t(
                "compliance.forms.importError",
                "Failed to import compliance report.",
              );
        setImportFeedback({
          type: "error",
          message,
        });
      } finally {
        setIsUploadingReport(false);
        setSelectedReportFile(null);
      }
    },
    [formStandard, organizationId, t],
  );

  const formatPercent = useCallback(
    (value: number) => `${clampPercent(value)}%`,
    [],
  );

  const dateFormatter = useMemo(
    () => new Intl.DateTimeFormat(locale || "en", { dateStyle: "medium" }),
    [locale],
  );
  const timeFormatter = useMemo(
    () => new Intl.DateTimeFormat(locale || "en", { timeStyle: "short" }),
    [locale],
  );

  const formatDate = useCallback(
    (value?: string | null) => {
      if (!value) return "—";
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return value;
      try {
        return dateFormatter.format(parsed);
      } catch {
        return parsed.toLocaleDateString();
      }
    },
    [dateFormatter],
  );

  const getQuestionLabel = useCallback(
    (category: ComplianceFormCategory, question: ComplianceFormQuestion) => {
      // Always prioritize text_en first
      const textEn = question.text_en && typeof question.text_en === "string" && question.text_en.trim().length > 0
        ? question.text_en.trim()
        : null;
      if (textEn) return textEn;
      
      // Then use normalizeQuestionLabel
      return normalizeQuestionLabel(category, question, locale || "en");
    },
    [locale],
  );

  const toggleCategory = useCallback((categoryId: string) => {
    setExpandedCategoryIds((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId],
    );
  }, []);

  const standardOptions = useMemo(() => {
    const normalizeCode = (code: string): string => {
      const canonical = getCanonicalStandardCode(code);
      if (canonical === "ISO27001") return "ISO 27001";
      if (canonical === "ISO42001") return "ISO/IEC 42001:2023";
      if (canonical === "SOC2") return "SOC 2";
      if (canonical === "NIS2") return "NIS 2";
      if (canonical === "PCI_DSS_2025") return "PCI DSS 2025";
      if (canonical === "SWIFT_CSCF_2025") return "SWIFT CSCF 2025";
      if (canonical === "SDLC_SECURITY") return "SDLC Security";
      if (canonical === "TECH_CONTROLS" || canonical === "TECHNICAL_CONTROLS") return "Technical Controls";
      if (canonical === "CIS") return "CIS Controls v8";
      if (canonical === "ENS_ES") return "ENS_ES";
      if (canonical === "BSI_DE" || canonical === "BSI") return "BSI_DE";
      if (canonical === "ENS_ANSSI" || canonical === "ANSSI_FR" || canonical === "ANSSI") return "ANSSI_FR";
      if (canonical === "KSBP_LT" || canonical === "KSBP") return "KSBP_LT";
      if (canonical === "NCA_EE" || canonical === "NCA") return "NCA_EE";
      return code;
    };

    // Build options: API standards + any from Starter guide not already in API (e.g. ISO27001)
    let filteredStandards: StandardOption[];
    if (availableStandards.length === 0 && selectedStandards.size > 0) {
      filteredStandards = Array.from(selectedStandards).map((name) => ({
        code: name,
        label: name,
        active: true,
      }));
    } else {
      const fromApi = selectedStandards.size === 0
        ? availableStandards
        : availableStandards.filter((option) => {
            const normalizedCode = normalizeCode(option.code);
            return selectedStandards.has(normalizedCode);
          });
      const apiNormalized = new Set(fromApi.map((o) => normalizeCode(o.code)));
      const fromStarter = Array.from(selectedStandards).filter((s) => !apiNormalized.has(s));
      filteredStandards = [
        ...fromApi,
        ...fromStarter.map((name) => ({ code: name, label: name, active: true })),
      ];
    }

    const mapped = collapseStandardsForUx(
      filteredStandards.map((opt) => ({
        code: opt.code,
        label: opt.label,
        name: opt.label,
        active: opt.active,
      })),
      { nis2Label: 'NIS 2' },
    ) as StandardOption[];

    return [
      {
        value: '',
        label: t('compliance.filters.allStandards', 'All Standards'),
      },
      ...mapped.map((option) => ({
        value: option.code,
        label: option.label ?? option.code,
      })),
    ];
  }, [availableStandards, selectedStandards, t]);

  const formCategoryOptions = useMemo(
    () => [
      {
        value: "all",
        label: t("compliance.filters.allCategories", "All Categories"),
      },
      ...formCategories.map((category) => ({
        value: category.code ?? category.id,
        label: category.name || humanize(category.code ?? category.id),
      })),
    ],
    [formCategories, t],
  );

  const visibleFormCategories = useMemo(() => {
    if (!formCategoryCode) return formCategories;
    return formCategories.filter((category) => category.code === formCategoryCode);
  }, [formCategories, formCategoryCode]);

  const selectedDashboardCategoryName = useMemo(() => {
    if (!dashboardCategory || dashboardCategory === "all") return null;
    const option = dashboardCategoryOptions.find(
      (entry) => entry.value === dashboardCategory,
    );
    return option?.label ?? null;
  }, [dashboardCategory, dashboardCategoryOptions]);

  const filteredSurveyRows = useMemo(() => {
    if (!dashboardCategory || dashboardCategory === "all") return surveyRows;
    const normalizedCode = dashboardCategory.toLowerCase();
    const normalizedName = selectedDashboardCategoryName?.toLowerCase() ?? null;
    return surveyRows.filter((row) => {
      const rowCategory = row.category?.toLowerCase?.() ?? "";
      if (normalizedName) {
        return rowCategory === normalizedName;
      }
      return rowCategory.includes(normalizedCode);
    });
  }, [surveyRows, dashboardCategory, selectedDashboardCategoryName]);

  if (loading) {
    return (
      <ComplianceCenterShell>
        <PageLayout pageScope="compliance-page" title={t("compliance.title", "Compliance Dashboard")} hideHeader={embeddedMode}>
          <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-[var(--ds-primary,#3b82f6)]" />
            <span>{t("common.loading", "Loading...")}</span>
          </div>
        </PageLayout>
      </ComplianceCenterShell>
    );
  }

  return (
    <ComplianceCenterShell>
    <PageLayout pageScope="compliance-page" title={t("compliance.title", "Compliance Dashboard")}
      hideHeader={embeddedMode}
      description={error ? <p className="text-sm text-red-500">{error}</p> : undefined}
      actions={!embeddedMode ? (
        standardOptions.length > 0 ? (
          <div className={pageStyles.compilanceActions}>
            <Select<string>
              data-testid="standard-select"
              label={t("compliance.filters.standard", "Standard")}
              className="text-sm"
              minWidth="180px"
              options={standardOptions.map((option) => ({
                value: option.value,
                label: option.label,
              }))}
              value={dashboardStandard ?? ""}
              onChange={(v) =>
                handleDashboardStandardChange(v === "" || v == null ? null : v)
              }
            />
            {dashboardCategoryOptions.length > 1 && (
              <Select<string>
                data-testid="category-select"
                label={t("compliance.filters.category", "Category")}
                className="text-sm"
                minWidth="180px"
                options={dashboardCategoryOptions.map((option) => ({
                  value: option.value,
                  label: option.label,
                }))}
                value={dashboardCategory ?? "all"}
                onChange={(v) => v != null && handleDashboardCategoryChange(v)}
              />
            )}
          </div>
        ) : undefined
      ) : undefined}
    >
      <div
        className={pageStyles.compliancePage}
        data-enterprise-embedded={compactEnterpriseEmbed ? "true" : undefined}
      >
      {!compactEnterpriseEmbed ? (
      <section className={pageStyles.kpiSection}>
        <div className={pageStyles.kpiSecurityRow}>
          {kpiCards.map((card) => (
            <SecurityCard
              key={card.id}
              className={pageStyles.kpiSecurityCard}
              title={card.title}
              percent={card.percent}
              color={card.color}
              size="small"
              chartSize={78}
              innerRadius={28}
              outerRadius={36}
              hoverDetail={
                <div className={pageStyles.kpiHoverDetail}>
                  <div className={pageStyles.kpiHoverPrimary}>{card.primary}</div>
                  <div className={pageStyles.kpiHoverSubtitle}>{card.subtitle}</div>
                </div>
              }
            />
          ))}
        </div>
      </section>
      ) : null}

      {!compactEnterpriseEmbed ? (
      <section className={pageStyles.auditReadinessSection}>
        <div className={pageStyles.auditReadinessCard}>
          <h3 className={pageStyles.auditReadinessTitle}>
            {t("compliance.auditReadiness.title", "Audit Readiness")}
          </h3>
          {featureFlags.COMPLIANCE_WORKFLOW_V1 ? (
            <div style={{ gridColumn: "1 / -1" }}>
              <p className="text-sm text-gray-600 mb-3">
                {t(
                  "compliance.auditReadiness.mergedIntoWorkflow",
                  "Audit readiness, scopes, blocking gaps, and certification workflow actions are managed in Operational Workflow — not in legacy questionnaires.",
                )}
              </p>
              {auditReadinessSummary ? (
                <p className="text-xs text-gray-500 mb-3">
                  {t("compliance.auditReadiness.quickSummary", "Readiness")}:{" "}
                  {Math.round(auditReadinessSummary?.readinessScore || 0)}% ·{" "}
                  {t("compliance.auditReadiness.blockingGaps", "Blocking gaps")}:{" "}
                  {auditReadinessSummary?.blockersCount ?? 0}
                </p>
              ) : null}
              <Button size="sm" variant="secondary" onClick={() => navigate("/compliance-workflow")}>
                {t("complianceWorkflow.openOperationalWorkflow", "Open Operational Workflow")}
              </Button>
            </div>
          ) : (
          <div className={pageStyles.auditReadinessGrid}>
            {embeddedMode ? (
              <p className="text-sm text-gray-600" style={{ gridColumn: "1 / -1" }}>
                {t(
                  "compliance.auditReadiness.embeddedOverviewHint",
                  "Summary scores and health metrics are shown in Compliance Center Overview above.",
                )}
              </p>
            ) : null}
            {!embeddedMode ? (
            <div>
              <div className={pageStyles.auditReadinessLabel}>
                {t("compliance.auditReadiness.status", "Status")}
              </div>
              <div className={pageStyles.auditReadinessValue}>
                {humanize(auditReadinessSummary?.readinessStatus || "NOT_READY")}
              </div>
            </div>
            ) : null}
            {!embeddedMode ? (
            <div>
              <div className={pageStyles.auditReadinessLabel}>
                {t("compliance.auditReadiness.score", "Readiness Score")}
              </div>
              <div className={pageStyles.auditReadinessValue}>
                {Math.round(auditReadinessSummary?.readinessScore || 0)}%
              </div>
            </div>
            ) : null}
            {!embeddedMode ? (
            <div>
              <div className={pageStyles.auditReadinessLabel}>
                {t("compliance.auditReadiness.blockingGaps", "Blocking Gaps")}
              </div>
              <div className={pageStyles.auditReadinessValue}>
                {auditReadinessSummary?.blockersCount ?? 0}
              </div>
            </div>
            ) : null}
            {!embeddedMode ? (
            <div>
              <div className={pageStyles.auditReadinessLabel}>
                {t("compliance.auditReadiness.missingEvidence", "Missing Evidence")}
              </div>
              <div className={pageStyles.auditReadinessValue}>
                {auditReadinessSummary?.missingEvidenceCount ?? 0}
              </div>
            </div>
            ) : null}
            {!embeddedMode ? (
            <div>
              <div className={pageStyles.auditReadinessLabel}>
                {t("compliance.auditReadiness.openRemediation", "Open Remediation")}
              </div>
              <div className={pageStyles.auditReadinessValue}>
                {auditReadinessSummary?.openCriticalRecommendations ?? 0}
              </div>
            </div>
            ) : null}
            {!embeddedMode ? (
            <div>
              <div className={pageStyles.auditReadinessLabel}>
                {t("compliance.auditReadiness.driftEvents", "Drift Events")}
              </div>
              <div className={pageStyles.auditReadinessValue}>
                {auditReadinessSummary?.driftEventsCount ?? 0}
              </div>
            </div>
            ) : null}
            {!embeddedMode ? (
            <div>
              <div className={pageStyles.auditReadinessLabel}>
                {t("compliance.auditReadiness.evidenceRequests", "Evidence Requests")}
              </div>
              <div className={pageStyles.auditReadinessValue}>
                {auditReadinessSummary?.evidenceRequestsCount ?? 0}
              </div>
            </div>
            ) : null}
            <div>
              <div className={pageStyles.auditReadinessLabel}>
                {t("compliance.auditReadiness.connectors", "Connector Framework")}
              </div>
              <div className={pageStyles.auditReadinessValue}>
                {auditReadinessSummary?.connectorsCount ?? 0}
              </div>
            </div>
            <div>
              <div className={pageStyles.auditReadinessLabel}>
                {t("compliance.auditReadiness.certificationWorkflows", "Certification Workflows")}
              </div>
              <div className={pageStyles.auditReadinessValue}>
                {auditReadinessSummary?.certificationWorkflowsCount ?? 0}
              </div>
            </div>
            <div>
              <div className={pageStyles.auditReadinessLabel}>
                {t("compliance.auditReadiness.certificationUnderReview", "Auditor Package Status")}
              </div>
              <div className={pageStyles.auditReadinessValue}>
                {auditReadinessSummary?.certificationUnderReviewCount ?? 0}
              </div>
            </div>
            <div>
              <div className={pageStyles.auditReadinessLabel}>
                {t("compliance.auditReadiness.certificationDecision", "Decision Status")}
              </div>
              <div className={pageStyles.auditReadinessValue}>
                {humanize(auditReadinessSummary?.certificationLatestStatus || "draft")}
              </div>
            </div>
            {!embeddedMode ? (
            <div>
              <div className={pageStyles.auditReadinessLabel}>
                {t("compliance.auditReadiness.aiRecommendations", "AI Recommendations")}
              </div>
              <div className={pageStyles.auditReadinessValue}>
                {auditReadinessSummary?.aiRecommendationCount ?? 0}
              </div>
            </div>
            ) : null}
            {!embeddedMode ? (
            <div>
              <div className={pageStyles.auditReadinessLabel}>
                {t("compliance.auditReadiness.aiNextSteps", "AI Next Steps")}
              </div>
              <div className={pageStyles.auditReadinessValue}>
                {auditReadinessSummary?.aiNextStepsCount ?? 0}
              </div>
            </div>
            ) : null}
            <div>
              <div className={pageStyles.auditReadinessLabel}>
                {t("compliance.auditReadiness.aiReadinessNarrative", "AI Readiness Summary")}
              </div>
              <div className={pageStyles.auditReadinessValue}>
                {(auditReadinessSummary?.aiReadinessNarrative || "").slice(0, 120) || "—"}
              </div>
            </div>
            {!embeddedMode ? (
            <div>
              <div className={pageStyles.auditReadinessLabel}>
                {t("compliance.auditReadiness.aiStaleEvidence", "AI Stale Evidence")}
              </div>
              <div className={pageStyles.auditReadinessValue}>
                {auditReadinessSummary?.aiStaleEvidenceCount ?? 0}
              </div>
            </div>
            ) : null}
            {!embeddedMode ? (
            <div>
              <div className={pageStyles.auditReadinessLabel}>
                {t("compliance.auditReadiness.aiMissingEvidence", "AI Missing Evidence")}
              </div>
              <div className={pageStyles.auditReadinessValue}>
                {auditReadinessSummary?.aiMissingEvidenceCount ?? 0}
              </div>
            </div>
            ) : null}
            {!embeddedMode ? (
            <div>
              <div className={pageStyles.auditReadinessLabel}>
                {t("compliance.auditReadiness.aiPolicyOverdue", "AI Policy Review Overdue")}
              </div>
              <div className={pageStyles.auditReadinessValue}>
                {auditReadinessSummary?.aiPolicyOverdueReviewCount ?? 0}
              </div>
            </div>
            ) : null}
          </div>
          )}
        </div>
      </section>
      ) : null}

      {/* All 5 charts in one horizontal row */}
      <section className={pageStyles.chartRow}>
        <div className={pageStyles.contentChartCard}>
          <h2 className={pageStyles.chartTitle}>
            {t("compliance.charts.trend", "Compliance Trend (Over Time)")}
          </h2>
          <div className={pageStyles.chartPlot}>
            {analyticsChartData.length === 0 ? (
              <div className="flex h-full items-center justify-center text-sm text-gray-500">
                <div className="text-center">
                  <div>{t("common.noData", "No data")}</div>
                  <div className="text-xs text-gray-400 mt-1">Select a standard to view compliance trend</div>
                </div>
              </div>
            ) : (
              <div className={pageStyles.chartPlotInner}>
                <ResponsiveContainer width="100%" height={COMPLIANCE_CHART_H}>
                  <LineChart
                    data={analyticsChartData}
                    margin={{ top: 12, right: 24, bottom: 8, left: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 12 }}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
                    <Tooltip
                      formatter={(value: number) => formatPercent(value)}
                      labelFormatter={(label) => label}
                    />
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke="#3b82f6"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>

        {isAllStandardsSelected && (
        <div className={pageStyles.contentChartCard}>
          <h2 className={pageStyles.chartTitle}>
            {t("compliance.charts.byStandard", "Compliance by Standard")}
          </h2>
          <div className={pageStyles.chartPlot}>
            {complianceByStandardData.length === 0 ? (
              <div className="flex h-full items-center justify-center text-sm text-gray-500">
                <div className="text-center">
                  <div className="mb-2">{t("compliance.noAssessments", "No assessments yet.")}</div>
                  <Button
                    type="button"
                    size="small"
                    color="primary"
                    onClick={() => {
                      const firstStandard = availableStandards?.[0]?.code;
                      if (firstStandard) {
                        navigate(`/compliance?standard=${firstStandard}`);
                      }
                    }}
                    className="text-xs px-4 py-2 transition-colors"
                  >
                    {t("compliance.startQuestionnaire", "Start questionnaire")}
                  </Button>
                </div>
              </div>
            ) : (
              <div className={pageStyles.chartPlotInner}>
                <ResponsiveContainer width="100%" height={COMPLIANCE_CHART_H}>
                  <BarChart
                    data={complianceByStandardData}
                    layout="vertical"
                    margin={{ top: 12, right: 24, bottom: 8, left: 35 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      type="number"
                      domain={[0, 100]}
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => formatPercent(Number(value))}
                    />
                    <YAxis
                      dataKey="name"
                      type="category"
                      tick={{ fontSize: 9 }}
                      width={30}
                      interval={0}
                      angle={0}
                      tickFormatter={(value: string) => {
                        if (value && value.length > 8) {
                          return value.substring(0, 8) + '...'
                        }
                        return value
                      }}
                    />
                    <Tooltip
                      formatter={(value: number) => formatPercent(value)}
                      labelFormatter={(label, payload) => {
                        const fullName = payload?.[0]?.payload?.fullName;
                        return fullName || label;
                      }}
                      contentStyle={{ maxWidth: '300px', wordWrap: 'break-word' }}
                    />
                    <Bar dataKey="value" radius={[4, 4, 4, 4]} barSize={18}>
                      {complianceByStandardData.map((entry, index) => (
                        <Cell
                          key={`${entry.code}-${index}`}
                          fill={mapThresholdColor(entry.value)}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
        )}

        {isAllStandardsSelected && (
        <div className={pageStyles.contentChartCard}>
          <h2 className={pageStyles.chartTitle}>
            {t("compliance.charts.maturity", "Control Maturity Map")}
          </h2>
          <div className={pageStyles.chartPlot}>
            {radarData.length === 0 ? (
              <div className="flex h-full items-center justify-center text-sm text-gray-500">
                <div className="text-center">
                  <div>{t("common.noData", "No data")}</div>
                  <div className="text-xs text-gray-400 mt-1">Select a standard to view control maturity</div>
                </div>
              </div>
            ) : (
              <div className={pageStyles.chartPlotInner}>
                <ResponsiveContainer width="100%" height={COMPLIANCE_CHART_H}>
                  <RadarChart
                    data={radarData}
                    margin={{ top: 20, right: 40, bottom: 20, left: 40 }}
                  >
                    <PolarGrid />
                    <PolarAngleAxis
                      dataKey="subject"
                      tick={{ fontSize: 10 }}
                      tickFormatter={(v: string) => truncateRadarLabel(v, 12)}
                    />
                    <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 10 }} />
                    <Radar
                      name={t("compliance.charts.maturityLabel", "Compliance")}
                      dataKey="value"
                      stroke="#6366f1"
                      fill="#6366f1"
                      fillOpacity={0.4}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
        )}

        {/* Category Maturity Map (Radar) - only for single standard; hidden in CC embedded view (duplicate) */}
        {!embeddedMode && !isAllStandardsSelected && dashboardStandard && (
        <div className={pageStyles.contentChartCard}>
          <h2 className={pageStyles.chartTitle}>
            {dashboardStandard && dashboardStandard !== 'all' && !isAllStandardsSelected
              ? `${humanize(dashboardStandard)} - ${t("compliance.charts.categoryMaturity", "Category Maturity Map")}`
              : t("compliance.charts.categoryMaturity", "Category Maturity Map")
            }
          </h2>
          <div className={pageStyles.chartPlot}>
            {!dashboardStandard || dashboardStandard === 'all' || isAllStandardsSelected ? (
              <div className="flex h-full items-center justify-center text-sm text-gray-500">
                <div className="text-center">
                  <div>{t("common.noData", "No data")}</div>
                  <div className="text-xs text-gray-400 mt-1">
                    {t("compliance.charts.selectStandard", "Select a standard to view category maturity")}
                  </div>
                </div>
              </div>
            ) : categoryRadarData.length === 0 ? (
              <div className="flex h-full items-center justify-center text-sm text-gray-500">
                <div className="text-center">
                  <div>{t("common.noData", "No data")}</div>
                  <div className="text-xs text-gray-400 mt-1">
                    No category data for {humanize(dashboardStandard)}
                  </div>
                  {dashboardCategoryOptions.length > 0 && (
                    <div className="text-xs text-gray-400 mt-1">
                      Available categories: {dashboardCategoryOptions.length}
                    </div>
                  )}
                  {surveyRows.length > 0 && (
                    <div className="text-xs text-gray-400 mt-1">
                      Survey rows: {surveyRows.length}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className={pageStyles.chartPlotInner}>
                <ResponsiveContainer width="100%" height={COMPLIANCE_CHART_H}>
                  <RadarChart
                    data={categoryRadarData}
                    margin={{ top: 20, right: 44, bottom: 20, left: 44 }}
                  >
                    <PolarGrid />
                    <PolarAngleAxis
                      dataKey="subject"
                      tick={{ fontSize: 10 }}
                      tickFormatter={(v: string) => truncateRadarLabel(v, 12)}
                    />
                    <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 10 }} />
                    <Radar
                      name={t("compliance.charts.maturityLabel", "Compliance")}
                      dataKey="value"
                      stroke="#10b981"
                      fill="#10b981"
                      fillOpacity={0.4}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
        )}

        <div className={pageStyles.contentChartCard}>
          <h2 className={pageStyles.chartTitle}>
            {t("compliance.charts.weakAreas", "Weak Areas (Lowest 5)")}
          </h2>
          <div className={pageStyles.chartPlot}>
            {weakAreas.length === 0 ? (
              <div className="flex h-full items-center justify-center text-sm text-gray-500">
                <div className="text-center">
                  <div>{t("common.noData", "No data")}</div>
                  <div className="text-xs text-gray-400 mt-1">Select a standard to view weak areas</div>
                </div>
              </div>
            ) : (
              <div className={pageStyles.chartPlotInner}>
                <ResponsiveContainer width="100%" height={COMPLIANCE_CHART_H}>
                  <BarChart
                    data={weakAreas}
                    layout="vertical"
                    margin={{ top: 12, right: 24, bottom: 8, left: 40 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      type="number"
                      domain={[0, 100]}
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => formatPercent(Number(value))}
                    />
                    <YAxis
                      dataKey="name"
                      type="category"
                      tick={{ fontSize: 9 }}
                      width={35}
                      interval={0}
                      angle={0}
                      tickFormatter={(value: string) => {
                        if (value && value.length > 10) {
                          return value.substring(0, 10) + '...'
                        }
                        return value ? value.replace(/[_\s]+/g, ' ').trim() : value
                      }}
                    />
                    <Tooltip
                      formatter={(value: number) => formatPercent(value)}
                      labelFormatter={(label, payload) => {
                        const fullName = payload?.[0]?.payload?.fullName;
                        return fullName || label;
                      }}
                      contentStyle={{ maxWidth: '300px', wordWrap: 'break-word' }}
                    />
                    <Bar dataKey="value" radius={[4, 4, 4, 4]} barSize={18}>
                      {weakAreas.map((entry, index) => (
                        <Cell key={`${entry.name}-${index}`} fill="#ef4444" />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>

        {/* Distribution - only for single standard */}
        {!isAllStandardsSelected && dashboardStandard && (
        <div className={pageStyles.contentChartCard}>
          <h2 className={pageStyles.chartTitle}>
            {(() => {
              const hasExt = extendedQuestionnairesData?.SELF_ASSESSMENT?.overallStats && (extendedQuestionnairesData.SELF_ASSESSMENT.overallStats.total ?? 0) > 0;
              return hasExt
                ? t("compliance.charts.distributionExtended", "Distribution (Yes / Partial / No / N/A)")
                : t("compliance.charts.distribution", "Distribution by Score (1–5)");
            })()}
          </h2>
          <div className={pageStyles.chartPlot}>
            {distributionChartData.length === 0 ? (
              <div className="flex h-full items-center justify-center text-sm text-gray-500">
                <div className="text-center">
                  <div>{t("common.noData", "No data")}</div>
                  <div className="text-xs text-gray-400 mt-1">Select a standard to view score distribution</div>
                </div>
              </div>
            ) : (
              <div className={pageStyles.chartPlotInner}>
                <ResponsiveContainer width="100%" height={COMPLIANCE_CHART_H}>
                  <BarChart
                    data={distributionChartData}
                    margin={{ top: 12, right: 24, bottom: 8, left: 24 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="score"
                      tick={{ fontSize: 12 }}
                      allowDecimals={false}
                      tickFormatter={(v) => {
                        const map: Record<number, string> = { 1: 'Yes', 2: 'Partial', 3: 'No', 4: 'N/A' };
                        return map[Number(v)] ?? String(v);
                      }}
                    />
                    <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                    <Tooltip
                      formatter={(value: number) => [
                        value,
                        t("compliance.charts.tooltipCount", "Responses"),
                      ]}
                      labelFormatter={(label) => {
                        const map: Record<string, string> = { 1: 'Yes', 2: 'Partial', 3: 'No', 4: 'N/A' };
                        return map[String(label)] ?? t("compliance.charts.scoreLabel", "Score {score}", { score: label });
                      }}
                    />
                    <Bar dataKey="count" maxBarSize={40} radius={[4, 4, 0, 0]}>
                      {distributionChartData.map((entry) => (
                        <Cell
                          key={entry.score}
                          fill={
                            Number(entry.score) <= 4
                              ? ({ 1: '#10b981', 2: '#facc15', 3: '#ef4444', 4: '#9ca3af' } as Record<number, string>)[Number(entry.score)] ?? '#9ca3af'
                              : SCORE_COLORS[String(entry.score)] ?? SCORE_COLORS["1"]
                          }
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
        )}
      </section>

      {/* Extended Questionnaires Status Charts — under main charts, one row, driven by Compliance Overview filter */}
      {!isAllStandardsSelected && dashboardStandard && dashboardStandard !== 'all' && (
      <section className={pageStyles.extendedSection}>
        <h2 className={pageStyles.extendedSectionTitle}>
          {t("compliance.extendedCharts.title", "Extended Questionnaires Status")}
        </h2>
        {(() => {
          const standardForCharts = dashboardStandard;
          const standardUpper = standardForCharts?.toUpperCase();
          const isGDPR = standardUpper === 'GDPR';
          const isSOC2 = standardUpper === 'SOC 2' || standardUpper === 'SOC2';
          const isNIS2Full = usesNis2ExtendedSelfAssessment(standardUpper);
          const isENS = standardUpper === 'NATIONAL_ENS_ES' || standardUpper === 'ENS' || standardUpper === 'ENS_ES';
          const isDORA = standardUpper === 'DORA';
          const isISO27001 = standardUpper === 'ISO27001' || standardUpper === 'ISO_27001' || standardUpper === 'ISO 27001' || standardUpper === 'ISO/IEC 27001';
          const isPciDss = standardUpper === 'PCI DSS 2025' || standardUpper === 'PCI_DSS_2025';
          const isSwiftCscf = standardUpper === 'SWIFT CSCF 2025' || standardUpper === 'SWIFT_CSCF_2025';
          const isSdlcSecurity = standardUpper === 'SDLC SECURITY' || standardUpper === 'SDLC_SECURITY';
          const isTechControls = standardUpper === 'TECHNICAL CONTROLS' || standardUpper === 'TECHNICAL_CONTROLS' || standardUpper === 'TECH_CONTROLS' || standardUpper === 'TECH CONTROLS';
          const isNationalProfile = ['BSI_DE', 'ANSSI_FR', 'ENS_ANSSI', 'KSBP_LT', 'NCA_EE'].includes(standardUpper || '');
          const isCIS = standardUpper === 'CIS';
          const isSupported = isGDPR || isSOC2 || isNIS2Full || isENS || isDORA || isISO27001 || isPciDss || isSwiftCscf || isSdlcSecurity || isTechControls || isNationalProfile || isCIS;

          if (isIso42001Selection(standardForCharts)) {
            const answered = Object.keys(extendedQuestionnairesData?.SELF_ASSESSMENT?.answers || {}).length;
            const chartState = iso42001UiState({
              loading: isoFullLoading || !isoFullSettled,
              error: isoFullError,
              definitionAvailable: isoFullAvailable,
              answered,
            });
            if (chartState !== "ready" || selfAssessmentRadarData.length === 0) {
              return (
                <div className="rounded-md border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
                  {chartState === "loading"
                    ? t("compliance.forms.iso42001.loading", "Loading questionnaire…")
                    : chartState === "error"
                      ? t("compliance.forms.iso42001.loadError", "Failed to load the questionnaire.")
                      : chartState === "empty"
                        ? t("compliance.forms.iso42001.emptyDefinition", "Questionnaire definition is unavailable")
                        : t("compliance.forms.iso42001.chartsAwaiting", "Complete assessment to see charts")}
                  {chartState === "error" ? (
                    <Button type="button" className="mt-3" onClick={() => setIso42001ReloadKey((value) => value + 1)}>
                      {t("compliance.forms.iso42001.retry", "Retry")}
                    </Button>
                  ) : null}
                </div>
              );
            }
            return (
              <div className={pageStyles.extendedChartRow}>
                <div className={pageStyles.contentChartCard}>
                  <h3 className={pageStyles.chartTitle}>
                    {t("compliance.charts.selfAssessmentCategoryMaturity", "Self-Assessment Category Maturity Map")}
                  </h3>
                  <div className={pageStyles.chartPlot}>
                    <div className={pageStyles.chartPlotInnerExtended}>
                      <ResponsiveContainer width="100%" height={EXTENDED_RADAR_H}>
                        <RadarChart data={selfAssessmentRadarData} margin={{ top: 20, right: 40, bottom: 20, left: 40 }}>
                          <PolarGrid />
                          <PolarAngleAxis dataKey="subject" tick={{ fontSize: 10 }} tickFormatter={(v: string) => truncateRadarLabel(v, 12)} />
                          <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 10 }} />
                          <Radar name={t("compliance.charts.maturityLabel", "Compliance")} dataKey="value" stroke="#6366f1" fill="#6366f1" fillOpacity={0.4} />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                        </RadarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>
            );
          }

          if (!isSupported) {
            return (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-700">
                {t("compliance.extendedCharts.selectSupported", "Extended questionnaire charts require completed assessment data.", { standard: standardForCharts || '' })}
              </div>
            );
          }

          return (
          <div className={pageStyles.extendedChartRow}>
            {/* Full Self-Assessment Questionnaire */}
            <div key="self-assessment" className={pageStyles.contentChartCard}>
              <h3 className={pageStyles.chartTitle}>
                {t(
                  "compliance.charts.selfAssessmentCategoryMaturity",
                  "Self-Assessment Category Maturity Map",
                )}
              </h3>
              <div className={pageStyles.chartPlot}>
                {(() => {
                  const hasData = extendedQuestionnairesData.SELF_ASSESSMENT !== null;
                  const hasRadarData = selfAssessmentRadarData.length > 0;
                  if (!hasData) {
                    return (
                      <div className="flex h-full items-center justify-center text-sm text-gray-500">
                        {t("compliance.extendedCharts.noData", "No data available. Please fill out the questionnaire.")}
                      </div>
                    );
                  }
                  if (!hasRadarData) {
                    return (
                      <div className="flex h-full items-center justify-center text-sm text-gray-500">
                        {t("compliance.extendedCharts.noBlockData", "Questionnaire data exists but no block statistics available. Please answer questions in blocks.")}
                      </div>
                    );
                  }
                  return (
                    <div className={pageStyles.chartPlotInnerExtended}>
                      <ResponsiveContainer width="100%" height={EXTENDED_RADAR_H}>
                        <RadarChart data={selfAssessmentRadarData} margin={{ top: 20, right: 40, bottom: 20, left: 40 }}>
                          <PolarGrid />
                          <PolarAngleAxis
                            dataKey="subject"
                            tick={{ fontSize: 10 }}
                            tickFormatter={(v: string) => truncateRadarLabel(v, 12)}
                          />
                          <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 10 }} />
                          <Radar
                            name={t("compliance.charts.maturityLabel", "Compliance")}
                            dataKey="value"
                            stroke="#6366f1"
                            fill="#6366f1"
                            fillOpacity={0.4}
                          />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                        </RadarChart>
                      </ResponsiveContainer>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Required Documents Checklist */}
            <div key="documents" className={pageStyles.contentChartCard}>
              <h3 className={pageStyles.chartTitle}>
                {t("compliance.forms.types.documents", "Required Documents Checklist")}
              </h3>
              <div className={pageStyles.chartPlot}>
                {(() => {
                  const hasData = extendedQuestionnairesData.DOCUMENTS !== null;
                  const hasRadarData = documentsRadarData.length > 0;
                  if (!hasData) {
                    return (
                      <div className="flex h-full items-center justify-center text-sm text-gray-500">
                        {t("compliance.extendedCharts.noData", "No data available. Please fill out the questionnaire.")}
                      </div>
                    );
                  }
                  if (!hasRadarData) {
                    return (
                      <div className="flex h-full items-center justify-center text-sm text-gray-500">
                        {t("compliance.extendedCharts.noBlockData", "Questionnaire data exists but no block statistics available. Please answer questions in blocks.")}
                      </div>
                    );
                  }
                  return (
                    <div className={pageStyles.chartPlotInnerExtended}>
                      <ResponsiveContainer width="100%" height={EXTENDED_RADAR_H}>
                        <RadarChart data={documentsRadarData} margin={{ top: 20, right: 40, bottom: 20, left: 40 }}>
                          <PolarGrid />
                          <PolarAngleAxis
                            dataKey="subject"
                            tick={{ fontSize: 10 }}
                            tickFormatter={(v: string) => truncateRadarLabel(v, 12)}
                          />
                          <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 10 }} />
                          <Radar
                            name={t("compliance.charts.maturityLabel", "Compliance")}
                            dataKey="value"
                            stroke="#10b981"
                            fill="#10b981"
                            fillOpacity={0.4}
                          />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                        </RadarChart>
                      </ResponsiveContainer>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Required Processes Checklist */}
            <div key="processes" className={pageStyles.contentChartCard}>
              <h3 className={pageStyles.chartTitle}>
                {t("compliance.forms.types.processes", "Required Processes Checklist")}
              </h3>
              <div className={pageStyles.chartPlot}>
                {(() => {
                  const hasData = extendedQuestionnairesData.PROCESSES !== null;
                  const hasRadarData = processesRadarData.length > 0;
                  if (!hasData) {
                    return (
                      <div className="flex h-full items-center justify-center text-sm text-gray-500">
                        {t("compliance.extendedCharts.noData", "No data available. Please fill out the questionnaire.")}
                      </div>
                    );
                  }
                  if (!hasRadarData) {
                    return (
                      <div className="flex h-full items-center justify-center text-sm text-gray-500">
                        {t("compliance.extendedCharts.noBlockData", "Questionnaire data exists but no block statistics available. Please answer questions in blocks.")}
                      </div>
                    );
                  }
                  return (
                    <div className={pageStyles.chartPlotInnerExtended}>
                      <ResponsiveContainer width="100%" height={EXTENDED_RADAR_H}>
                        <RadarChart data={processesRadarData} margin={{ top: 20, right: 40, bottom: 20, left: 40 }}>
                          <PolarGrid />
                          <PolarAngleAxis
                            dataKey="subject"
                            tick={{ fontSize: 10 }}
                            tickFormatter={(v: string) => truncateRadarLabel(v, 12)}
                          />
                          <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 10 }} />
                          <Radar
                            name={t("compliance.charts.maturityLabel", "Compliance")}
                            dataKey="value"
                            stroke="#f59e0b"
                            fill="#f59e0b"
                            fillOpacity={0.4}
                          />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                        </RadarChart>
                      </ResponsiveContainer>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Roles & Departments Checklist */}
            <div key="roles" className={pageStyles.contentChartCard}>
              <h3 className={pageStyles.chartTitle}>
                {t("compliance.forms.types.roles", "Roles & Departments Checklist")}
              </h3>
              <div className={pageStyles.chartPlot}>
                {(() => {
                  const hasData = extendedQuestionnairesData.ROLES !== null;
                  const hasRadarData = rolesRadarData.length > 0;
                  if (!hasData) {
                    return (
                      <div className="flex h-full items-center justify-center text-sm text-gray-500">
                        {t("compliance.extendedCharts.noData", "No data available. Please fill out the questionnaire.")}
                      </div>
                    );
                  }
                  if (!hasRadarData) {
                    return (
                      <div className="flex h-full items-center justify-center text-sm text-gray-500">
                        {t("compliance.extendedCharts.noBlockData", "Questionnaire data exists but no block statistics available. Please answer questions in blocks.")}
                      </div>
                    );
                  }
                  return (
                    <div className={pageStyles.chartPlotInnerExtended}>
                      <ResponsiveContainer width="100%" height={EXTENDED_RADAR_H}>
                        <RadarChart data={rolesRadarData} margin={{ top: 20, right: 40, bottom: 20, left: 40 }}>
                          <PolarGrid />
                          <PolarAngleAxis
                            dataKey="subject"
                            tick={{ fontSize: 10 }}
                            tickFormatter={(v: string) => truncateRadarLabel(v, 12)}
                          />
                          <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 10 }} />
                          <Radar
                            name={t("compliance.charts.maturityLabel", "Compliance")}
                            dataKey="value"
                            stroke="#8b5cf6"
                            fill="#8b5cf6"
                            fillOpacity={0.4}
                          />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                        </RadarChart>
                      </ResponsiveContainer>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
          );
        })()}
      </section>
      )}

      <section className={pageStyles.questionnairesSection}>
         <div className={pageStyles.questionnairesCard}>
           <div>
             <h2 className={pageStyles.questionnairesHeader}>
               {t("compliance.forms.heading", "Questionnaires")}
             </h2>
             <p className={pageStyles.questionnairesSubtitle}>
               {t(
                 "compliance.forms.subtitle",
                'Expand a category to answer questions inline. Click "Save responses" to persist your answers.',
               )}
             </p>
           </div>

          {/* Legacy data banner: when we have short/ComplianceRecord data but no Extended for selected standard */}
          {formStandard && (() => {
            const hasLegacyData = (summary?.details?.length ?? 0) > 0 || (summary?.totalHealth ?? 0) > 0 || (complianceByStandardData?.length ?? 0) > 0;
            const hasExtendedData = !!(
              (extendedQuestionnairesData?.SELF_ASSESSMENT?.answers && Object.keys(extendedQuestionnairesData.SELF_ASSESSMENT.answers).length > 0) ||
              (extendedQuestionnairesData?.ANNEX_A_CONTROLS?.answers && Object.keys(extendedQuestionnairesData.ANNEX_A_CONTROLS.answers || {}).length > 0) ||
              (extendedQuestionnairesData?.GDPR_CONTROLS?.answers && Object.keys(extendedQuestionnairesData.GDPR_CONTROLS?.answers || {}).length > 0) ||
              (extendedQuestionnairesData?.DORA_CONTROLS?.answers && Object.keys(extendedQuestionnairesData.DORA_CONTROLS?.answers || {}).length > 0) ||
              (extendedQuestionnairesData?.ENS_CONTROLS?.answers && Object.keys(extendedQuestionnairesData.ENS_CONTROLS?.answers || {}).length > 0) ||
              (extendedQuestionnairesData?.NIS2_CONTROLS?.answers && Object.keys(extendedQuestionnairesData.NIS2_CONTROLS?.answers || {}).length > 0) ||
              (extendedQuestionnairesData?.PCI_DSS_2025_CONTROLS?.answers && Object.keys(extendedQuestionnairesData.PCI_DSS_2025_CONTROLS?.answers || {}).length > 0) ||
              (extendedQuestionnairesData?.SWIFT_CSCF_2025_CONTROLS?.answers && Object.keys(extendedQuestionnairesData.SWIFT_CSCF_2025_CONTROLS?.answers || {}).length > 0)
            );
            if (hasLegacyData && !hasExtendedData && ['NIS2_CONTROLS', 'GDPR_CONTROLS', 'DORA_CONTROLS', 'ENS_CONTROLS', 'PCI_DSS_2025_CONTROLS', 'SWIFT_CSCF_2025_CONTROLS', 'SOC2_CONTROLS', 'ANNEX_A_CONTROLS'].includes(questionnaireType)) {
              const migrationSupported = isMigrationSupportedForStandard(formStandard);
              return (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200 mb-4">
                  <strong>{t("compliance.legacyBanner.title", "Legacy data imported")}</strong>
                  <p className="mt-1">
                    {t("compliance.legacyBanner.message", "Historical compliance data from previous surveys is displayed. Please complete the Full Self-Assessment Questionnaire below to ensure accurate metrics and recommendations.")}
                  </p>
                  {migrationSupported ? (
                  <Button
                    type="button"
                    size="small"
                    color="primary"
                    onClick={async () => {
                      if (!formStandard || !organizationId || migrating) return;
                      setMigrating(true);
                      try {
                        const stdCode = formStandard === 'ISO 27001' || formStandard === 'ISO/IEC 27001' ? 'ISO_27001' : formStandard === 'ISO/IEC 42001:2023' || formStandard === 'ISO/IEC 42001' || formStandard === 'ISO 42001' ? 'ISO_42001' : formStandard === 'SOC 2' ? 'SOC2' : formStandard === 'PCI DSS 2025' ? 'PCI_DSS_2025' : formStandard === 'SWIFT CSCF 2025' ? 'SWIFT_CSCF_2025' : formStandard === 'NIS2' || formStandard === 'NIS 2' ? 'NIS2' : formStandard;
                        const res = await apiService.migrateShortToExtended(stdCode, organizationId);
                        if (res?.success) {
                          setExtendedDataRefreshKey((k) => k + 1);
                          setDashboardRefreshKey((k) => k + 1);
                          setFormRefreshKey((k) => k + 1);
                        } else {
                          alert(t("compliance.legacyBanner.migrateError", "Migration failed: {error}", { error: (res as any)?.message || 'Unknown error' }));
                        }
                      } catch (err: any) {
                        alert(t("compliance.legacyBanner.migrateError", "Migration failed: {error}", { error: err?.message || String(err) }));
                      } finally {
                        setMigrating(false);
                      }
                    }}
                    disabled={migrating}
                    className="mt-3 bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:bg-amber-400"
                  >
                    {migrating ? t("compliance.legacyBanner.migrating", "Migrating...") : t("compliance.legacyBanner.migrateButton", "Migrate to Full Assessment")}
                  </Button>
                  ) : (
                    <p className="mt-3 text-xs opacity-90">
                      {t("compliance.legacyBanner.migrationUnsupported", "Automatic migration is not available for this standard. Use the Full Self-Assessment Questionnaire below.")}
                    </p>
                  )}
                </div>
              );
            }
            return null;
          })()}

          <div className={pageStyles.toolbarRow}>
            {!embeddedMode ? (
              <Select<string>
                data-testid="inline-standard-select"
                label={t("compliance.filters.standard", "Standard")}
                className="text-sm"
                minWidth="180px"
                options={standardOptions.map((option) => ({
                  value: option.value,
                  label: option.label,
                }))}
                value={formStandard ?? ""}
                onChange={(v) => handleFormStandardChange(v === "" || v == null ? null : v)}
              />
            ) : null}

            {formStandard && (
              <Select<string>
                data-testid="questionnaire-type-select"
                label={t("compliance.forms.questionnaireType", "Questionnaire Type")}
                className="text-sm"
                minWidth="240px"
                options={questionnaireTypeOptions}
                value={questionnaireType}
                onChange={(v) => {
                  if (v != null)
                    setQuestionnaireType(
                      v as typeof questionnaireType,
                    );
                }}
              />
            )}

            <div className={pageStyles.uploadPanel}>
              <FilePicker
                accept=".csv,.xlsx,.xls,.pdf"
                value={selectedReportFile}
                onChange={handleComplianceReportPicked}
                chooseLabel={
                  isUploadingReport
                    ? t("common.uploading", "Uploading...")
                    : t("compliance.actions.uploadReport", "Upload report")
                }
                noFileLabel={t("reports.noFileChosen", "No file chosen")}
                disabled={isUploadingReport}
                onBeforePick={validateReportUploadContext}
              />
              {importFeedback && (
                <span
                  className={
                    importFeedback.type === "success"
                      ? pageStyles.feedbackOk
                      : pageStyles.feedbackErr
                  }
                  aria-live="polite"
                >
                  {importFeedback.message}
                </span>
              )}
            </div>
          </div>
 
          {formStandard ? (() => {
            const standardUpper = formStandard.toUpperCase();
            const isGDPR = standardUpper === 'GDPR';
            const isSOC2 = standardUpper === 'SOC 2' || standardUpper === 'SOC2';
            const isISO27001 = standardUpper === 'ISO27001' || standardUpper === 'ISO_27001' || standardUpper === 'ISO 27001' || standardUpper === 'ISO/IEC 27001';
            // Support both NIS2 (regular) and NIS2_FULL for full questionnaire
            const isNIS2Full = usesNis2ExtendedSelfAssessment(standardUpper);
            const isENS = standardUpper === 'NATIONAL_ENS_ES' || standardUpper === 'ENS' || standardUpper === 'ENS_ES';
            const isDORA = standardUpper === 'DORA';
            const isPciDss = standardUpper === 'PCI DSS 2025' || standardUpper === 'PCI_DSS_2025';
            const isSwiftCscf = standardUpper === 'SWIFT CSCF 2025' || standardUpper === 'SWIFT_CSCF_2025';
            const isSdlcSecurity = standardUpper === 'SDLC SECURITY' || standardUpper === 'SDLC_SECURITY';
            const isTechControls = standardUpper === 'TECHNICAL CONTROLS' || standardUpper === 'TECHNICAL_CONTROLS' || standardUpper === 'TECH_CONTROLS' || standardUpper === 'TECH CONTROLS';
            const isNationalProfile = ['BSI_DE', 'ANSSI_FR', 'ENS_ANSSI', 'KSBP_LT', 'NCA_EE'].includes(standardUpper || '');

            if (isIso42001Selection(formStandard)) {
              const quickAnswered = Object.values(answers).some((bucket) =>
                Object.values(bucket || {}).some((value) => Number(value) > 0),
              );
              const questionIds = isoFullBlocks.flatMap((block) => (block.questions || []).map((question) => question.id));
              const progress = iso42001FullProgress(questionIds, extendedAnswers);
              return (
                <Iso42001Questionnaire
                  mode={questionnaireType === "QUICK" ? "QUICK" : "SELF_ASSESSMENT"}
                  quickState={iso42001UiState({
                    loading: isoQuickLoading,
                    error: isoQuickError,
                    definitionAvailable: isoQuickAvailable,
                    answered: quickAnswered ? 1 : 0,
                  })}
                  fullState={iso42001UiState({
                    loading: isoFullLoading || !isoFullSettled,
                    error: isoFullError,
                    definitionAvailable: isoFullAvailable,
                    answered: progress.answered,
                  })}
                  visibleFormCategories={visibleFormCategories}
                  quickAnswers={answers}
                  expandedCategoryIds={expandedCategoryIds}
                  onToggleCategory={toggleCategory}
                  onQuickAnswer={setAnswer}
                  onSaveQuick={() => saveAnswers()}
                  getQuestionLabel={getQuestionLabel}
                  fullBlocks={isoFullBlocks}
                  fullAnswers={extendedAnswers}
                  fullProgress={progress}
                  onFullAnswer={(questionId, answer) => {
                    setExtendedAnswers((prev) => ({ ...prev, [questionId]: answer }));
                  }}
                  onSaveFull={async (data) => {
                    if (!organizationId) throw new Error("Missing organization");
                    const savedProgress = iso42001FullProgress(questionIds, data.answers);
                    const response = await apiService.saveExtendedQuestionnaire({
                      standardCode: "ISO42001",
                      questionnaireType: "SELF_ASSESSMENT",
                      answers: data.answers,
                      blockStats: data.blockStats,
                      overallStats: { ...data.overallStats, total: savedProgress.total },
                      organizationId,
                    });
                    if (!response.success) throw new Error("save failed");
                    setExtendedAnswers(data.answers);
                    setExtendedLastSaved(response.data?.lastSavedAt || new Date().toISOString());
                    setExtendedQuestionnairesData((prev) => ({
                      ...prev,
                      SELF_ASSESSMENT: {
                        answers: data.answers,
                        blockStats: data.blockStats,
                        overallStats: { ...data.overallStats, total: savedProgress.total },
                      },
                    }));
                  }}
                  onRetry={() => setIso42001ReloadKey((value) => value + 1)}
                  disabled={extendedLoading}
                />
              );
            }
            
            let blocks = null;
            if (isGDPR) {
              blocks = GDPR_QUESTIONNAIRES[questionnaireType as keyof typeof GDPR_QUESTIONNAIRES];
            } else if (isSOC2) {
              const type = questionnaireType === 'SOC2_CONTROLS' ? 'SELF_ASSESSMENT' : questionnaireType;
              blocks = SOC2_QUESTIONNAIRES[type as keyof typeof SOC2_QUESTIONNAIRES] ?? null;
            } else if (isISO27001) {
              blocks = ISO27001_QUESTIONNAIRES[questionnaireType as keyof typeof ISO27001_QUESTIONNAIRES];
            } else if (isNIS2Full && questionnaireType !== 'NIS2_CONTROLS') {
              blocks = NIS2_FULL_QUESTIONNAIRES[questionnaireType as keyof typeof NIS2_FULL_QUESTIONNAIRES] ?? (questionnaireType === 'SELF_ASSESSMENT' ? NIS2_FULL_QUESTIONNAIRES.SELF_ASSESSMENT : null);
            } else if (isDORA && questionnaireType !== 'DORA_CONTROLS') {
              blocks = DORA_QUESTIONNAIRES[questionnaireType as keyof typeof DORA_QUESTIONNAIRES] ?? null;
            } else if (isENS && questionnaireType !== 'ENS_CONTROLS') {
              blocks = ENS_MEDIO_QUESTIONNAIRES[questionnaireType as keyof typeof ENS_MEDIO_QUESTIONNAIRES] ?? null;
            } else if (isPciDss && questionnaireType === 'SELF_ASSESSMENT') {
              blocks = PCI_DSS_2025_SELF_ASSESSMENT_BLOCKS;
            } else if (isSwiftCscf && questionnaireType === 'SELF_ASSESSMENT') {
              blocks = SWIFT_CSCF_2025_SELF_ASSESSMENT_BLOCKS;
            } else if (isSdlcSecurity && questionnaireType === 'SELF_ASSESSMENT') {
              blocks = SDLC_SECURITY_SELF_ASSESSMENT_BLOCKS;
            } else if (isTechControls && questionnaireType === 'SELF_ASSESSMENT') {
              blocks = TECHNICAL_CONTROLS_SELF_ASSESSMENT_BLOCKS;
            } else if (isNationalProfile && questionnaireType === 'SELF_ASSESSMENT') {
              blocks = standardUpper === 'BSI_DE' ? BSI_DE_SELF_ASSESSMENT_BLOCKS
                : (standardUpper === 'ANSSI_FR' || standardUpper === 'ENS_ANSSI') ? ANSSI_FR_SELF_ASSESSMENT_BLOCKS
                : standardUpper === 'KSBP_LT' ? KSBP_LT_SELF_ASSESSMENT_BLOCKS
                : standardUpper === 'NCA_EE' ? NCA_EE_SELF_ASSESSMENT_BLOCKS
                : null;
            }
            
            const isCIS = standardUpper === 'CIS';
            
            if (!isGDPR && !isSOC2 && !isISO27001 && !isNIS2Full && !isENS && !isDORA && !isPciDss && !isSwiftCscf && !isSdlcSecurity && !isTechControls && !isNationalProfile && !isCIS) {
              return (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-700">
                  {t('compliance.forms.extendedNotAvailable', 'Full Self-Assessment Questionnaire is available for this standard.', { standard: formStandard || '' })}
                </div>
              );
            }

            if (questionnaireType === 'ANNEX_A_CONTROLS' && isISO27001) {
              const standardCodeForApi = 'ISO27001';
              return (
                <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
                  <AnnexAControlsPanel
                    key={`annexa-${organizationId}-${extendedLastSaved ?? 'init'}`}
                    initialStatuses={(extendedAnswers || {}) as unknown as Record<string, import('../data/iso27001AnnexACatalog').ControlStatus>}
                    onSave={async (statuses) => {
                      if (!organizationId) return;
                      try {
                        setExtendedLoading(true);
                        const response = await apiService.saveExtendedQuestionnaire({
                          standardCode: standardCodeForApi,
                          questionnaireType: 'ANNEX_A_CONTROLS',
                          answers: statuses,
                          organizationId,
                        });
                        if (response.success && response.data) {
                          setExtendedLastSaved(response.data?.lastSavedAt || new Date().toISOString());
                          setExtendedAnswers(statuses as unknown as Record<string, ExtendedQuestionnaireAnswer>);
                          setExtendedQuestionnairesData((prev) => ({
                            ...prev,
                            ANNEX_A_CONTROLS: {
                              answers: statuses,
                              blockStats: response.data?.blockStats || {},
                              overallStats: response.data?.overallStats || { yes: 0, no: 0, n_a: 0, total: 0 },
                            },
                          }));
                          setDashboardRefreshKey((k) => k + 1);
                          setFormRefreshKey((k) => k + 1);
                        }
                      } catch (err) {
                        console.error('Failed to save Annex A:', err);
                        alert(t('compliance.forms.errors.save', 'Failed to save questionnaire'));
                      } finally {
                        setExtendedLoading(false);
                      }
                    }}
                    disabled={extendedLoading}
                  />
                </div>
              );
            }

            if (questionnaireType === 'NIS2_CONTROLS' && isNIS2Full) {
              const standardCodeForApi = 'NIS2';
              return (
                <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
                  <Nis2ControlsPanel
                    key={`nis2-ctrl-${organizationId}-${extendedLastSaved ?? 'init'}`}
                    initialStatuses={(extendedAnswers || {}) as unknown as Record<string, import('../data/nis2ControlsCatalog').Nis2ControlStatus>}
                    onSave={async (statuses) => {
                      if (!organizationId) return;
                      try {
                        setExtendedLoading(true);
                        const response = await apiService.saveExtendedQuestionnaire({
                          standardCode: standardCodeForApi,
                          questionnaireType: 'NIS2_CONTROLS',
                          answers: statuses,
                          organizationId,
                        });
                        if (response.success && response.data) {
                          setExtendedLastSaved(response.data?.lastSavedAt || new Date().toISOString());
                          setExtendedAnswers(statuses as unknown as Record<string, ExtendedQuestionnaireAnswer>);
                          setExtendedQuestionnairesData((prev) => ({
                            ...prev,
                            NIS2_CONTROLS: {
                              answers: statuses,
                              blockStats: response.data?.blockStats || {},
                              overallStats: response.data?.overallStats || { yes: 0, no: 0, n_a: 0, total: 0 },
                            },
                          }));
                          setDashboardRefreshKey((k) => k + 1);
                          setFormRefreshKey((k) => k + 1);
                        }
                      } catch (err) {
                        console.error('Failed to save NIS2 Controls:', err);
                        alert(t('compliance.forms.errors.save', 'Failed to save questionnaire'));
                      } finally {
                        setExtendedLoading(false);
                      }
                    }}
                    disabled={extendedLoading}
                  />
                </div>
              );
            }

            if (questionnaireType === 'PCI_DSS_2025_CONTROLS' && isPciDss) {
              const standardCodeForApi = 'PCI_DSS_2025';
              return (
                <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
                  <PciDssControlsPanel
                    key={`pci-ctrl-${organizationId}-${extendedLastSaved ?? 'init'}`}
                    initialStatuses={(extendedAnswers || {}) as unknown as Record<string, import('../data/pciDss2025ControlsCatalog').PciDssControlStatus>}
                    onSave={async (statuses) => {
                      if (!organizationId) return;
                      try {
                        setExtendedLoading(true);
                        const response = await apiService.saveExtendedQuestionnaire({
                          standardCode: standardCodeForApi,
                          questionnaireType: 'PCI_DSS_2025_CONTROLS',
                          answers: statuses,
                          organizationId,
                        });
                        if (response.success && response.data) {
                          setExtendedLastSaved(response.data?.lastSavedAt || new Date().toISOString());
                          setExtendedAnswers(statuses as unknown as Record<string, ExtendedQuestionnaireAnswer>);
                          setExtendedQuestionnairesData((prev) => ({
                            ...prev,
                            PCI_DSS_2025_CONTROLS: {
                              answers: statuses,
                              blockStats: response.data?.blockStats || {},
                              overallStats: response.data?.overallStats || { yes: 0, no: 0, n_a: 0, total: 0 },
                            },
                          }));
                          setDashboardRefreshKey((k) => k + 1);
                          setFormRefreshKey((k) => k + 1);
                        }
                      } catch (err) {
                        console.error('Failed to save PCI DSS Controls:', err);
                        alert(t('compliance.forms.errors.save', 'Failed to save questionnaire'));
                      } finally {
                        setExtendedLoading(false);
                      }
                    }}
                    disabled={extendedLoading}
                  />
                </div>
              );
            }

            if (questionnaireType === 'SWIFT_CSCF_2025_CONTROLS' && isSwiftCscf) {
              const standardCodeForApi = 'SWIFT_CSCF_2025';
              return (
                <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
                  <SwiftCscfControlsPanel
                    key={`swift-ctrl-${organizationId}-${extendedLastSaved ?? 'init'}`}
                    initialStatuses={(extendedAnswers || {}) as unknown as Record<string, import('../data/swiftCscf2025ControlsCatalog').SwiftCscfControlStatus>}
                    onSave={async (statuses) => {
                      if (!organizationId) return;
                      try {
                        setExtendedLoading(true);
                        const response = await apiService.saveExtendedQuestionnaire({
                          standardCode: standardCodeForApi,
                          questionnaireType: 'SWIFT_CSCF_2025_CONTROLS',
                          answers: statuses,
                          organizationId,
                        });
                        if (response.success && response.data) {
                          setExtendedLastSaved(response.data?.lastSavedAt || new Date().toISOString());
                          setExtendedAnswers(statuses as unknown as Record<string, ExtendedQuestionnaireAnswer>);
                          setExtendedQuestionnairesData((prev) => ({
                            ...prev,
                            SWIFT_CSCF_2025_CONTROLS: {
                              answers: statuses,
                              blockStats: response.data?.blockStats || {},
                              overallStats: response.data?.overallStats || { yes: 0, no: 0, n_a: 0, total: 0 },
                            },
                          }));
                          setDashboardRefreshKey((k) => k + 1);
                          setFormRefreshKey((k) => k + 1);
                        }
                      } catch (err) {
                        console.error('Failed to save SWIFT CSCF Controls:', err);
                        alert(t('compliance.forms.errors.save', 'Failed to save questionnaire'));
                      } finally {
                        setExtendedLoading(false);
                      }
                    }}
                    disabled={extendedLoading}
                  />
                </div>
              );
            }

            if (questionnaireType === 'ENS_CONTROLS' && isENS) {
              const standardCodeForApi = 'ENS_ES';
              return (
                <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
                  <EnsControlsPanel
                    key={`ens-ctrl-${organizationId}-${extendedLastSaved ?? 'init'}`}
                    initialStatuses={(extendedAnswers || {}) as unknown as Record<string, import('../data/ensControlsCatalog').EnsControlStatus>}
                    onSave={async (statuses) => {
                      if (!organizationId) return;
                      try {
                        setExtendedLoading(true);
                        const response = await apiService.saveExtendedQuestionnaire({
                          standardCode: standardCodeForApi,
                          questionnaireType: 'ENS_CONTROLS',
                          answers: statuses,
                          organizationId,
                        });
                        if (response.success && response.data) {
                          setExtendedLastSaved(response.data?.lastSavedAt || new Date().toISOString());
                          setExtendedAnswers(statuses as unknown as Record<string, ExtendedQuestionnaireAnswer>);
                          setExtendedQuestionnairesData((prev) => ({
                            ...prev,
                            ENS_CONTROLS: {
                              answers: statuses,
                              blockStats: response.data?.blockStats || {},
                              overallStats: response.data?.overallStats || { yes: 0, no: 0, n_a: 0, total: 0 },
                            },
                          }));
                          setDashboardRefreshKey((k) => k + 1);
                          setFormRefreshKey((k) => k + 1);
                        }
                      } catch (err) {
                        console.error('Failed to save ENS Controls:', err);
                        alert(t('compliance.forms.errors.save', 'Failed to save questionnaire'));
                      } finally {
                        setExtendedLoading(false);
                      }
                    }}
                    disabled={extendedLoading}
                  />
                </div>
              );
            }

            if (questionnaireType === 'DORA_CONTROLS' && isDORA) {
              const standardCodeForApi = 'DORA';
              return (
                <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
                  <DoraControlsPanel
                    key={`dora-ctrl-${organizationId}-${extendedLastSaved ?? 'init'}`}
                    initialStatuses={(extendedAnswers || {}) as unknown as Record<string, import('../data/doraControlsCatalog').DoraControlStatus>}
                    onSave={async (statuses) => {
                      if (!organizationId) return;
                      try {
                        setExtendedLoading(true);
                        const response = await apiService.saveExtendedQuestionnaire({
                          standardCode: standardCodeForApi,
                          questionnaireType: 'DORA_CONTROLS',
                          answers: statuses,
                          organizationId,
                        });
                        if (response.success && response.data) {
                          setExtendedLastSaved(response.data?.lastSavedAt || new Date().toISOString());
                          setExtendedAnswers(statuses as unknown as Record<string, ExtendedQuestionnaireAnswer>);
                          setExtendedQuestionnairesData((prev) => ({
                            ...prev,
                            DORA_CONTROLS: {
                              answers: statuses,
                              blockStats: response.data?.blockStats || {},
                              overallStats: response.data?.overallStats || { yes: 0, no: 0, n_a: 0, total: 0 },
                            },
                          }));
                          setDashboardRefreshKey((k) => k + 1);
                          setFormRefreshKey((k) => k + 1);
                        }
                      } catch (err) {
                        console.error('Failed to save DORA Controls:', err);
                        alert(t('compliance.forms.errors.save', 'Failed to save questionnaire'));
                      } finally {
                        setExtendedLoading(false);
                      }
                    }}
                    disabled={extendedLoading}
                  />
                </div>
              );
            }

            if (questionnaireType === 'GDPR_CONTROLS' && isGDPR) {
              const standardCodeForApi = 'GDPR';
              return (
                <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
                  <GdpControlsPanel
                    key={`gdpr-ctrl-${organizationId}-${extendedLastSaved ?? 'init'}`}
                    initialStatuses={(extendedAnswers || {}) as unknown as Record<string, import('../data/gdprControlsCatalog').GdpControlStatus>}
                    onSave={async (statuses) => {
                      if (!organizationId) return;
                      try {
                        setExtendedLoading(true);
                        const response = await apiService.saveExtendedQuestionnaire({
                          standardCode: standardCodeForApi,
                          questionnaireType: 'GDPR_CONTROLS',
                          answers: statuses,
                          organizationId,
                        });
                        if (response.success && response.data) {
                          setExtendedLastSaved(response.data?.lastSavedAt || new Date().toISOString());
                          setExtendedAnswers(statuses as unknown as Record<string, ExtendedQuestionnaireAnswer>);
                          setExtendedQuestionnairesData((prev) => ({
                            ...prev,
                            GDPR_CONTROLS: {
                              answers: statuses,
                              blockStats: response.data?.blockStats || {},
                              overallStats: response.data?.overallStats || { yes: 0, no: 0, n_a: 0, total: 0 },
                            },
                          }));
                          setDashboardRefreshKey((k) => k + 1);
                          setFormRefreshKey((k) => k + 1);
                        }
                      } catch (err) {
                        console.error('Failed to save GDPR Controls:', err);
                        alert(t('compliance.forms.errors.save', 'Failed to save questionnaire'));
                      } finally {
                        setExtendedLoading(false);
                      }
                    }}
                    disabled={extendedLoading}
                  />
                </div>
              );
            }
            
            if (!blocks || blocks.length === 0) {
              return (
                <div className="rounded-md border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
                  {t('compliance.forms.noExtendedData', 'No data available for this questionnaire type.')}
                </div>
              );
            }
            
            return (
              <ExtendedQuestionnaire
                key={`${formStandard}-${questionnaireType}`}
                standardCode={formStandard}
                questionnaireType={questionnaireType}
                blocks={blocks}
                initialAnswers={extendedAnswers}
                onAnswerChange={(questionId, answer) => {
                  setExtendedAnswers((prev) => ({ ...prev, [questionId]: answer }));
                }}
                onSave={async (data) => {
                  if (!organizationId || !formStandard) return;
                  const blockStats = data?.blockStats ?? {};
                  const overallStats = data?.overallStats ?? { yes: 0, no: 0, n_a: 0, total: 0 };
                  try {
                    setExtendedLoading(true);
                    const response = await apiService.saveExtendedQuestionnaire({
                      standardCode: standardCodeForApi ?? formStandard,
                      questionnaireType: questionnaireType,
                      answers: data?.answers ?? {},
                      blockStats,
                      overallStats,
                      organizationId: organizationId,
                    });
                  if (response.success && response.data) {
                    setExtendedLastSaved(response.data.lastSavedAt || new Date().toISOString());
                    // Update extended questionnaires data for charts
                    setExtendedQuestionnairesData((prev) => ({
                      ...prev,
                      [questionnaireType]: {
                        answers: data?.answers ?? {},
                        blockStats,
                        overallStats,
                      },
                    }));
                  }
                } catch (error) {
                  console.error('Failed to save extended questionnaire:', error);
                  alert(t('compliance.forms.errors.save', 'Failed to save questionnaire'));
                } finally {
                  setExtendedLoading(false);
                }
              }}
                disabled={extendedLoading}
              />
            );
          })() : (
            <div className="rounded-md border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
              {t("compliance.forms.selectStandard", "Select a standard to load questionnaires.")}
            </div>
          )}
        </div>
      </section>

      {/* Recommendations Panel — below questionnaire blocks, when a standard is selected */}
      {formStandard && dashboardStandard && dashboardStandard !== "all" && !isAllStandardsSelected && (
        <section className="space-y-4 mt-6">
          <Suspense fallback={null}>
            <LazyRecommendationsPanel
              recommendations={complianceRecommendations}
              title={t("reco.title", "Recommendations")}
              showFilter={true}
              maxItems={15}
              weakAreaMap={recommendationWeakAreaMap}
            />
          </Suspense>
        </section>
      )}
      </div>
    </PageLayout>
    </ComplianceCenterShell>
  );
};

export default Compliance;
